import { useState, useEffect, useRef, useCallback } from "react";
import { initializeApp } from "firebase/app";
import {
  getFirestore, collection, doc, addDoc, updateDoc,
  deleteDoc, onSnapshot, serverTimestamp, query, orderBy, where, setDoc
} from "firebase/firestore";

// ─── Firebase ─────────────────────────────────────────────────────────────────
const firebaseConfig = {
  apiKey: "AIzaSyC0z7feofoXc2WMpTHoma5az__ayZ0jZas",
  authDomain: "intranet-lommax.firebaseapp.com",
  projectId: "intranet-lommax",
  storageBucket: "intranet-lommax.firebasestorage.app",
  messagingSenderId: "87564233658",
  appId: "1:87564233658:web:b7e01a402e4c56f95a4b83"
};
const fbApp = initializeApp(firebaseConfig);
const db = getFirestore(fbApp);

// ─── Tokens ───────────────────────────────────────────────────────────────────
const T = {
  navy:"#1B2A4A", teal:"#4BA3C3", tealLight:"#E8F4F8",
  bg:"#EEF2F7", white:"#FFFFFF", success:"#0EA572",
  warn:"#F59E0B", danger:"#EF4444", purple:"#7C3AED",
  gray:"#6B7280", gray2:"#9CA3AF", border:"#E2E8F0",
};

// ─── PIN ──────────────────────────────────────────────────────────────────────
const PINS = {
  "0000":{ role:"manager", name:"Gestionnaire" },
  "1111":{ role:"prestataire", name:"Jean-Marc", metier:"Ménage" },
  "2222":{ role:"prestataire", name:"Sophie", metier:"Linge" },
  "3333":{ role:"prestataire", name:"Michel", metier:"Maintenance" },
};

// ─── Kit items ────────────────────────────────────────────────────────────────
const KIT_ITEMS = [
  { id:"cafe", nom:"Café", icon:"☕", unite:"doses" },
  { id:"the", nom:"Thé", icon:"🍵", unite:"sachets" },
  { id:"biscuit", nom:"Biscuits", icon:"🍪", unite:"sachets" },
  { id:"eponge", nom:"Éponges", icon:"🧽", unite:"unités" },
  { id:"pastille", nom:"Pastilles LV", icon:"🫧", unite:"past." },
  { id:"poubelle", nom:"Sacs poubelle", icon:"🗑", unite:"sacs" },
];

// ─── Services additionnels ────────────────────────────────────────────────────
const SERVICES_BASE = [
  { id:"apero", nom:"Pack apéro", icon:"🥂", prix:25, desc:"Charcuterie, fromages, crackers" },
  { id:"cremant", nom:"Crémant d'Alsace", icon:"🍾", prix:18, desc:"Bouteille crémant local" },
  { id:"chien", nom:"Animaux (chien)", icon:"🐕", prix:15, desc:"Supplément animaux" },
  { id:"enfants", nom:"Pack enfants", icon:"🧸", prix:20, desc:"Chaise haute, lit bébé, jouets" },
  { id:"teletravail", nom:"Pack télétravail", icon:"💻", prix:10, desc:"Écran sup., câbles, bureau" },
  { id:"petitdej", nom:"Petit-déjeuner", icon:"🥐", prix:12, desc:"Viennoiseries, confitures, jus" },
];

const RELAIS = [
  "Point relais Masevaux — Tabac Presse, 3 rue de la Paix",
  "Point relais Mulhouse — Pressing Central, 14 rue du Sauvage",
  "Point relais Colmar — Laverie du Marché, 7 place Mairie",
  "Point relais Thann — Tabac, 2 rue Gal de Gaulle",
];

const METIERS = ["Ménage","Linge","Maintenance","Jardinage","Plomberie","Électricité","Accueil","Autre"];
const TYPES_LOG = ["Appartement","Maison","Studio","Villa","Chalet"];
const TYPES_PPL = ["Ménage","Linge","Maintenance","Jardinage","Livraison","Accueil","Autre"];
const TYPES_CAL = ["Vérification logement","Maintenance / réparation","RDV personnel","Service additionnel"];
const PRIOS = [{ value:"normale",label:"Normale" },{ value:"haute",label:"Haute" },{ value:"urgente",label:"Urgente" }];

const MONTHS = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
const DAYS_SHORT = ["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"];

// ─── Firebase hooks ───────────────────────────────────────────────────────────
function useCol(col) {
  const [data, setData] = useState([]);
  useEffect(() => {
    const q = query(collection(db, col), orderBy("createdAt","desc"));
    return onSnapshot(q, snap => setData(snap.docs.map(d => ({ id:d.id,...d.data() }))), ()=>{});
  }, [col]);
  return data;
}

// ─── UI atoms ─────────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Raleway:wght@700;800&family=Inter:wght@400;500;600;700&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Inter',system-ui,sans-serif;background:${T.bg};color:${T.navy};font-size:14px;-webkit-tap-highlight-color:transparent}
  input,select,textarea{font-family:inherit}
  ::-webkit-scrollbar{width:3px;height:3px}
  ::-webkit-scrollbar-thumb{background:#D1D5DB;border-radius:2px}
`;

const Btn = ({ onClick,children,color=T.teal,sm,danger,outline,disabled,full,style:sx={} }) => (
  <button onClick={disabled?undefined:onClick} style={{
    display:"inline-flex",alignItems:"center",justifyContent:"center",gap:5,
    padding:sm?"6px 12px":"9px 16px", fontSize:sm?12:13, fontWeight:700, borderRadius:10,
    cursor:disabled?"not-allowed":"pointer", opacity:disabled?0.4:1,
    border:outline?`1.5px solid ${danger?T.danger:color}`:"none",
    background:outline?"transparent":danger?T.danger:color,
    color:outline?(danger?T.danger:color):"#fff",
    width:full?"100%":"auto", transition:"all .15s", ...sx,
  }}>{children}</button>
);

const Input = ({ label,value,onChange,placeholder,type="text",required,min,max,small }) => (
  <div style={{ marginBottom:12 }}>
    {label && <label style={{ display:"block",fontSize:12,fontWeight:700,color:T.navy,marginBottom:5 }}>{label}{required&&" *"}</label>}
    <input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} min={min} max={max}
      style={{ width:"100%",padding:small?"7px 10px":"9px 12px",border:`1.5px solid ${T.border}`,borderRadius:10,fontSize:small?12:13,outline:"none",background:T.white,color:T.navy }} />
  </div>
);

const Sel = ({ label,value,onChange,options,required }) => (
  <div style={{ marginBottom:12 }}>
    {label && <label style={{ display:"block",fontSize:12,fontWeight:700,color:T.navy,marginBottom:5 }}>{label}{required&&" *"}</label>}
    <select value={value} onChange={e=>onChange(e.target.value)}
      style={{ width:"100%",padding:"9px 12px",border:`1.5px solid ${T.border}`,borderRadius:10,fontSize:13,outline:"none",background:T.white,color:T.navy }}>
      {options.map(o=><option key={o.value??o} value={o.value??o}>{o.label??o}</option>)}
    </select>
  </div>
);

const Bdg = ({ children,color=T.teal,bg }) => (
  <span style={{ display:"inline-flex",alignItems:"center",padding:"2px 9px",borderRadius:20,fontSize:11,fontWeight:700,background:bg||color+"22",color }}>{children}</span>
);

const Card = ({ children,accent,style:sx={},onClick }) => (
  <div onClick={onClick} style={{
    background:T.white,borderRadius:14,padding:"14px 16px",marginBottom:10,
    border:`1px solid ${T.border}`,boxShadow:"0 1px 4px rgba(0,0,0,.05)",
    borderLeft:accent?`4px solid ${accent}`:undefined,
    cursor:onClick?"pointer":"default",...sx,
  }}>{children}</div>
);

const Modal = ({ title,subtitle,onClose,children,footer,wide }) => (
  <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,.55)",zIndex:1000,display:"flex",alignItems:"flex-end",justifyContent:"center" }}
    onClick={e=>e.target===e.currentTarget&&onClose()}>
    <div style={{ background:T.white,borderRadius:"18px 18px 0 0",width:"100%",maxWidth:wide?640:480,maxHeight:"92vh",display:"flex",flexDirection:"column",boxShadow:"0 -8px 30px rgba(0,0,0,.2)" }}>
      <div style={{ display:"flex",justifyContent:"center",padding:"10px 0 0" }}>
        <div style={{ width:36,height:4,borderRadius:2,background:T.border }} />
      </div>
      <div style={{ padding:"12px 20px 10px",borderBottom:`1px solid ${T.border}` }}>
        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center" }}>
          <div>
            <div style={{ fontFamily:"Raleway",fontWeight:800,fontSize:17,color:T.navy }}>{title}</div>
            {subtitle&&<div style={{ fontSize:12,color:T.gray,marginTop:2 }}>{subtitle}</div>}
          </div>
          <button onClick={onClose} style={{ background:T.bg,border:"none",width:30,height:30,borderRadius:"50%",cursor:"pointer",fontSize:18,display:"flex",alignItems:"center",justifyContent:"center",color:T.gray }}>×</button>
        </div>
      </div>
      <div style={{ flex:1,overflowY:"auto",padding:"16px 20px" }}>{children}</div>
      {footer&&<div style={{ padding:"12px 20px",borderTop:`1px solid ${T.border}`,display:"flex",gap:8,justifyContent:"flex-end" }}>{footer}</div>}
    </div>
  </div>
);

const G2 = ({ children }) => <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10 }}>{children}</div>;
const Empty = ({ icon,text }) => <div style={{ textAlign:"center",padding:"32px 16px",color:T.gray2 }}><div style={{ fontSize:32,marginBottom:8 }}>{icon}</div><div style={{ fontSize:13,fontWeight:600 }}>{text}</div></div>;
const SectionTitle = ({ children }) => <div style={{ fontFamily:"Raleway",fontWeight:800,fontSize:18,color:T.navy,marginBottom:14 }}>{children}</div>;
const H3 = ({ children,color=T.navy }) => <div style={{ fontSize:14,fontWeight:700,color,marginBottom:8 }}>{children}</div>;

// ─── LOGIN ────────────────────────────────────────────────────────────────────
function Login({ onLogin }) {
  const [pin,setPin] = useState("");
  const [err,setErr] = useState(false);
  const [shake,setShake] = useState(false);
  const add = d => {
    const n = pin+d;
    if(n.length>4) return;
    setPin(n);
    if(n.length===4){
      const u = PINS[n];
      if(u){ setTimeout(()=>{ onLogin(u); setPin(""); setErr(false); },150); }
      else{ setErr(true); setShake(true); setTimeout(()=>{ setPin(""); setErr(false); setShake(false); },800); }
    }
  };
  return (
    <div style={{ minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:`linear-gradient(145deg,#0F1C33,${T.navy} 45%,${T.teal})`,padding:16 }}>
      <style>{css}</style>
      <div style={{ background:T.white,borderRadius:22,padding:"36px 28px",width:"100%",maxWidth:320,textAlign:"center",boxShadow:"0 30px 80px rgba(0,0,0,.4)",transform:shake?"translateX(-5px)":"none",transition:"transform .08s" }}>
        <div style={{ width:56,height:56,background:`linear-gradient(135deg,${T.navy},${T.teal})`,borderRadius:16,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 14px",fontSize:26 }}>🏡</div>
        <div style={{ fontFamily:"Raleway",fontWeight:800,fontSize:24,color:T.navy,letterSpacing:2 }}>LOMMAX</div>
        <div style={{ fontSize:12,color:T.gray2,marginTop:3,marginBottom:18 }}>Portail intranet — Code PIN</div>
        <div style={{ fontSize:28,letterSpacing:14,color:err?T.danger:T.navy,minHeight:38,transition:"color .2s" }}>
          {[0,1,2,3].map(i=><span key={i} style={{ opacity:i<pin.length?1:.2 }}>{i<pin.length?"●":"○"}</span>)}
        </div>
        {err&&<div style={{ color:T.danger,fontSize:12,marginBottom:6,fontWeight:600 }}>Code incorrect</div>}
        <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginTop:14 }}>
          {[1,2,3,4,5,6,7,8,9,"",0,"⌫"].map((d,i)=>(
            <button key={i} onClick={()=>d==="⌫"?setPin(p=>p.slice(0,-1)):d!==""&&add(String(d))}
              style={{ padding:"14px 0",fontSize:19,fontWeight:700,borderRadius:12,border:`1.5px solid ${T.border}`,background:d===""?"transparent":T.bg,color:T.navy,cursor:d===""?"default":"pointer",fontFamily:"Raleway",opacity:d===""?0:1 }}>{d}</button>
          ))}
        </div>
        <div style={{ background:T.bg,borderRadius:10,padding:"10px 12px",marginTop:18,textAlign:"left",border:`1px solid ${T.border}` }}>
          <div style={{ fontSize:11,fontWeight:700,color:T.gray,marginBottom:5 }}>Codes de démo</div>
          {[["0000","Gestionnaire"],["1111","Jean-Marc"],["2222","Sophie"],["3333","Michel"]].map(([p,n])=>(
            <div key={p} onClick={()=>{ setPin(""); setTimeout(()=>onLogin(PINS[p]),50); }}
              style={{ display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:12,cursor:"pointer",borderBottom:`1px solid ${T.border}` }}>
              <span style={{ fontWeight:700,color:T.navy }}>{p}</span>
              <span style={{ color:T.teal,fontWeight:600 }}>{n} →</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── SIGNATURE PAD ────────────────────────────────────────────────────────────
function SigPad({ onSave, onClear }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const last = useRef({x:0,y:0});
  const [empty,setEmpty] = useState(true);
  const pos = (e,c) => {
    const r=c.getBoundingClientRect();
    const sx=c.width/r.width, sy=c.height/r.height;
    if(e.touches) return {x:(e.touches[0].clientX-r.left)*sx,y:(e.touches[0].clientY-r.top)*sy};
    return {x:(e.clientX-r.left)*sx,y:(e.clientY-r.top)*sy};
  };
  const start=e=>{e.preventDefault();const c=ref.current,p=pos(e,c);drawing.current=true;last.current=p;const ctx=c.getContext("2d");ctx.beginPath();ctx.arc(p.x,p.y,1.5,0,Math.PI*2);ctx.fillStyle=T.navy;ctx.fill();setEmpty(false);};
  const move=e=>{e.preventDefault();if(!drawing.current)return;const c=ref.current,p=pos(e,c),ctx=c.getContext("2d");ctx.beginPath();ctx.moveTo(last.current.x,last.current.y);ctx.lineTo(p.x,p.y);ctx.strokeStyle=T.navy;ctx.lineWidth=2.5;ctx.lineCap="round";ctx.lineJoin="round";ctx.stroke();last.current=p;};
  const end=e=>{e.preventDefault();drawing.current=false;if(!empty)onSave(ref.current.toDataURL());};
  const clear=()=>{ref.current.getContext("2d").clearRect(0,0,ref.current.width,ref.current.height);setEmpty(true);onClear();};
  return (
    <div style={{ border:`2px dashed ${T.border}`,borderRadius:10,background:"#F8FAFC",position:"relative",overflow:"hidden" }}>
      <canvas ref={ref} width={480} height={110} onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end} onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        style={{ display:"block",cursor:"crosshair",touchAction:"none",width:"100%",height:110 }}/>
      {empty&&<div style={{ position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",pointerEvents:"none",gap:4 }}>
        <span style={{ fontSize:22,opacity:.25 }}>✍️</span>
        <span style={{ fontSize:11,color:T.gray2,fontWeight:600 }}>Signez ici avec votre doigt ou la souris</span>
      </div>}
      {!empty&&<button onClick={clear} style={{ position:"absolute",top:7,right:7,background:"rgba(255,255,255,.95)",border:`1px solid ${T.border}`,color:T.gray,padding:"3px 9px",borderRadius:7,cursor:"pointer",fontSize:11,fontWeight:600 }}>Effacer</button>}
    </div>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
function Dashboard({ ppls,logements,prestataires,stocks,taches,commandes,kits,docsObligatoires={} }) {
  const alertes = stocks.filter(s=>s.quantite<=s.minimum);
  const candidats = ppls.filter(p=>p.candidats?.length>0&&p.statut==="disponible");
  const pendingServices = commandes.filter(c=>c.statut==="en_attente");
  const stats = [
    { icon:"🏡",val:logements.length,lbl:"Logements",c:T.teal },
    { icon:"👷",val:prestataires.filter(p=>p.actif).length,lbl:"Prestataires",c:T.success },
    { icon:"📋",val:ppls.filter(p=>p.statut!=="clôturé").length,lbl:"PPL actifs",c:T.warn },
    { icon:"⚠️",val:alertes.length,lbl:"Alertes stock",c:T.danger },
    { icon:"✅",val:taches.filter(t=>t.statut!=="terminé").length,lbl:"Tâches ouvertes",c:T.purple },
    { icon:"🛎",val:pendingServices.length,lbl:"Services en attente",c:"#0369A1" },
  ];
  return (
    <div>
      <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:20 }}>
        {stats.map(s=>(
          <div key={s.lbl} style={{ background:T.white,borderRadius:12,padding:"12px 8px",textAlign:"center",border:`1px solid ${T.border}`,boxShadow:"0 1px 4px rgba(0,0,0,.04)" }}>
            <div style={{ fontSize:20 }}>{s.icon}</div>
            <div style={{ fontFamily:"Raleway",fontSize:24,fontWeight:800,color:s.c,lineHeight:1.1 }}>{s.val}</div>
            <div style={{ fontSize:10,color:T.gray,fontWeight:600,marginTop:2,lineHeight:1.3 }}>{s.lbl}</div>
          </div>
        ))}
      </div>
      {candidats.length>0&&<div style={{ marginBottom:16 }}>
        <H3 color={T.warn}>🙋 Candidatures en attente ({candidats.length})</H3>
        {candidats.map(p=><Card key={p.id} accent={T.warn}>
          <div style={{ fontWeight:700 }}>{p.titre}</div>
          <div style={{ color:T.warn,fontSize:12,marginTop:2 }}>👷 {p.candidats.join(", ")}</div>
          {p.nbVoyageurs>0&&<div style={{ color:T.gray,fontSize:12,marginTop:2 }}>👥 {p.nbVoyageurs} voyageur{p.nbVoyageurs>1?"s":""}</div>}
        </Card>)}
      </div>}
      {pendingServices.length>0&&<div style={{ marginBottom:16 }}>
        <H3 color="#0369A1">🛎 Services additionnels en attente ({pendingServices.length})</H3>
        {pendingServices.slice(0,3).map((c,i)=><Card key={i} accent={T.teal}>
          <div style={{ fontWeight:700 }}>{c.icon} {c.service}</div>
          <div style={{ color:T.gray,fontSize:12 }}>📍 {c.logement} · 📅 {c.dateArrivee} · 👥 {c.nomVoyageur}</div>
        </Card>)}
      </div>}
      {/* Alerte docs obligatoires */}
      {prestataires&&docsObligatoires&&(()=>{
        const missing=prestataires.filter(p=>p.actif&&missingDocsCount(docsObligatoires[p.id])>0);
        if(!missing.length) return null;
        return <div style={{ background:"#FEF2F2",border:`1px solid ${T.danger}`,borderRadius:10,padding:"10px 14px",marginBottom:16 }}>
          <H3 color={T.danger}>⚠️ Documents obligatoires manquants ({missing.length} prestataire{missing.length>1?"s":""})</H3>
          {missing.map(p=>{
            const n=missingDocsCount(docsObligatoires[p.id]);
            return <div key={p.id} style={{ fontSize:12,color:"#374151",padding:"3px 0" }}><strong>{p.prenom} {p.nom}</strong> — {n} document{n>1?"s":""} manquant{n>1?"s":""}</div>;
          })}
        </div>;
      })()}
      {alertes.length>0&&<div style={{ marginBottom:16 }}>
        <H3 color={T.danger}>📦 Ruptures stock ({alertes.length})</H3>
        {alertes.map(s=><Card key={s.id} accent={T.danger}>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center" }}>
            <div><div style={{ fontWeight:700 }}>{s.nom}</div><div style={{ color:T.gray,fontSize:12 }}>📍 {s.logement}</div></div>
            <span style={{ fontWeight:800,color:T.danger,fontSize:18 }}>{s.quantite} <span style={{ fontSize:11 }}>{s.unite}</span></span>
          </div>
        </Card>)}
      </div>}
      <H3>📋 PPL récents</H3>
      {ppls.slice(0,5).map(p=><Card key={p.id}>
        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",gap:8 }}>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:600 }}>{p.titre}</div>
            <div style={{ color:T.gray,fontSize:12,marginTop:2,display:"flex",gap:8,flexWrap:"wrap" }}>
              {p.logement&&<span>📍 {p.logement}</span>}
              {p.date&&<span>📅 {p.date}</span>}
              {p.nbVoyageurs>0&&<span>👥 {p.nbVoyageurs} voy.</span>}
            </div>
          </div>
          <Bdg color={{disponible:T.teal,assigné:T.success,"clôturé":T.gray}[p.statut]||T.teal}>{p.statut}</Bdg>
        </div>
      </Card>)}
    </div>
  );
}

// ─── LOGEMENTS ────────────────────────────────────────────────────────────────
function Logements({ logements }) {
  const [modal,setModal] = useState(null);
  const [form,setForm] = useState({});
  const f = k=>v=>setForm(p=>({...p,[k]:v}));
  const fi = k=>v=>setForm(p=>({...p,impekkable:{...p.impekkable,[k]:v}}));
  const emptyImp = ()=>({ actif:false,pointRelais:"",statut:"",dateDispo:"",nbSacs:0,notes:"" });
  const openAdd = ()=>{ setForm({ nom:"",adresse:"",type:"Appartement",chambres:1,capacite:2,notes:"",impekkable:emptyImp() }); setModal("add"); };
  const openEdit = l=>{ setForm({...l,impekkable:l.impekkable||emptyImp()}); setModal(l.id); };
  const save = async()=>{
    if(!form.nom?.trim()) return;
    const d={...form,chambres:Number(form.chambres),capacite:Number(form.capacite),updatedAt:serverTimestamp()};
    if(modal==="add") await addDoc(collection(db,"logements"),{...d,createdAt:serverTimestamp()});
    else await updateDoc(doc(db,"logements",modal),d);
    setModal(null);
  };
  const del = async id=>{ if(window.confirm("Supprimer ce logement ?")) await deleteDoc(doc(db,"logements",id)); };
  const tc = { Appartement:{bg:"#DBEAFE",c:"#1E40AF"},Maison:{bg:"#D1FAE5",c:"#065F46"},Studio:{bg:"#FEF3C7",c:"#92400E"},Villa:{bg:"#F3E8FF",c:"#6B21A8"},Chalet:{bg:"#FCE7F3",c:"#9D174D"} };
  return (
    <div>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14 }}>
        <SectionTitle>Logements ({logements.length})</SectionTitle>
        <Btn sm onClick={openAdd}>+ Ajouter</Btn>
      </div>
      {logements.length===0&&<Empty icon="🏡" text="Aucun logement."/>}
      {logements.map(l=>{
        const t=tc[l.type]||{bg:"#E0F2FE",c:"#0369A1"};
        const imp=l.impekkable||emptyImp();
        return <Card key={l.id} accent={T.teal}>
          <div style={{ display:"flex",justifyContent:"space-between",gap:10 }}>
            <div style={{ flex:1 }}>
              <div style={{ display:"flex",alignItems:"center",gap:7,marginBottom:4,flexWrap:"wrap" }}>
                <span style={{ fontWeight:700,fontSize:15 }}>{l.nom}</span>
                <Bdg color={t.c} bg={t.bg}>{l.type}</Bdg>
                {imp.actif&&<Bdg color="#065F46" bg="#D1FAE5">IMPEKKABLE</Bdg>}
              </div>
              {l.adresse&&<div style={{ color:T.gray,fontSize:12,marginBottom:2 }}>📍 {l.adresse}</div>}
              <div style={{ color:T.gray,fontSize:12,display:"flex",gap:10 }}><span>🛏 {l.chambres} ch.</span><span>👤 {l.capacite} pers.</span></div>
              {l.notes&&<div style={{ color:T.gray2,fontSize:11,marginTop:4,fontStyle:"italic" }}>{l.notes}</div>}
            </div>
            <div style={{ display:"flex",gap:5 }}>
              <Btn sm outline onClick={()=>openEdit(l)}>✏️</Btn>
              <Btn sm danger onClick={()=>del(l.id)}>🗑</Btn>
            </div>
          </div>
          {imp.actif&&<div style={{ marginTop:10,padding:"9px 12px",background:"#EFF6FF",borderRadius:9,border:"1px solid #BFDBFE" }}>
            <div style={{ fontSize:12,fontWeight:700,color:"#1E40AF",marginBottom:4 }}>
              <span style={{ background:T.navy,color:"#fff",borderRadius:5,padding:"1px 6px",fontSize:10,fontWeight:800,marginRight:6 }}>IMPEKKABLE</span>
              {imp.pointRelais?imp.pointRelais.split("—")[0]:"Point relais à définir"}
            </div>
            {imp.statut==="dispo"?<span style={{ background:"#D1FAE5",color:"#065F46",padding:"3px 9px",borderRadius:8,fontSize:11,fontWeight:700 }}>✅ Linge dispo{imp.nbSacs>0?` — ${imp.nbSacs} sac${imp.nbSacs>1?"s":""}`:""}</span>
            :imp.statut==="attente"?<span style={{ background:"#FEF3C7",color:"#92400E",padding:"3px 9px",borderRadius:8,fontSize:11,fontWeight:700 }}>⏳ Apporté ultérieurement{imp.dateDispo?` — ${imp.dateDispo}`:""}</span>
            :<span style={{ color:T.gray2,fontSize:11 }}>Statut non défini</span>}
          </div>}
        </Card>;
      })}
      {modal&&<Modal title={modal==="add"?"Nouveau logement":"Modifier le logement"} onClose={()=>setModal(null)}
        footer={<><Btn sm outline onClick={()=>setModal(null)}>Annuler</Btn><Btn sm onClick={save} disabled={!form.nom?.trim()}>Enregistrer</Btn></>}>
        <Input label="Nom *" value={form.nom||""} onChange={f("nom")} placeholder="Ex : Suite Yellow" required/>
        <Input label="Adresse" value={form.adresse||""} onChange={f("adresse")} placeholder="Rue, code postal, ville"/>
        <Sel label="Type" value={form.type||"Appartement"} onChange={f("type")} options={TYPES_LOG}/>
        <G2><Input label="Chambres" value={form.chambres||1} onChange={f("chambres")} type="number"/><Input label="Capacité" value={form.capacite||2} onChange={f("capacite")} type="number"/></G2>
        <Input label="Notes" value={form.notes||""} onChange={f("notes")} placeholder="Infos complémentaires..."/>
        <div style={{ marginTop:14,paddingTop:12,borderTop:`1px solid ${T.border}` }}>
          <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:10 }}>
            <span style={{ background:T.navy,color:"#fff",borderRadius:5,padding:"2px 7px",fontSize:10,fontWeight:800 }}>IMPEKKABLE</span>
            <span style={{ fontSize:13,fontWeight:700 }}>Linge externalisé</span>
            <label style={{ marginLeft:"auto",display:"flex",alignItems:"center",gap:5,cursor:"pointer" }}>
              <input type="checkbox" checked={!!form.impekkable?.actif} onChange={e=>fi("actif")(e.target.checked)} style={{ width:16,height:16,accentColor:T.teal }}/>
              <span style={{ fontSize:12,fontWeight:700,color:form.impekkable?.actif?T.success:T.gray2 }}>{form.impekkable?.actif?"Activé":"Désactivé"}</span>
            </label>
          </div>
          {form.impekkable?.actif&&<>
            <Sel label="Point relais" value={form.impekkable.pointRelais||""} onChange={fi("pointRelais")} options={[{value:"",label:"— Choisir —"},...RELAIS.map(r=>({value:r,label:r}))]}/>
            {form.impekkable.pointRelais&&<>
              <label style={{ fontSize:12,fontWeight:700,color:T.navy,marginBottom:8,display:"block" }}>Statut du linge</label>
              <div style={{ display:"flex",gap:8,marginBottom:12 }}>
                {[{v:"dispo",l:"✅ Dispo en relais"},{v:"attente",l:"⏳ Apporté plus tard"}].map(opt=>(
                  <button key={opt.v} onClick={()=>fi("statut")(opt.v)}
                    style={{ flex:1,padding:"8px 6px",borderRadius:9,border:`1.5px solid ${form.impekkable.statut===opt.v?(opt.v==="dispo"?T.success:T.warn):T.border}`,background:form.impekkable.statut===opt.v?(opt.v==="dispo"?"#D1FAE5":"#FEF3C7"):T.white,fontSize:12,fontWeight:700,cursor:"pointer",color:form.impekkable.statut===opt.v?(opt.v==="dispo"?"#065F46":"#92400E"):T.gray }}>{opt.l}</button>
                ))}
              </div>
              {form.impekkable.statut==="dispo"&&<Input label="Nombre de sacs" value={form.impekkable.nbSacs||0} onChange={fi("nbSacs")} type="number"/>}
              {form.impekkable.statut==="attente"&&<Input label="Date prévue" value={form.impekkable.dateDispo||""} onChange={fi("dateDispo")} type="date"/>}
              <Input label="Notes" value={form.impekkable.notes||""} onChange={fi("notes")} placeholder="Ex : Retrait avant 10h..."/>
            </>}
          </>}
        </div>
      </Modal>}
    </div>
  );
}

// ─── PRESTATAIRES ─────────────────────────────────────────────────────────────
function Prestataires({ prestataires }) {
  const [modal,setModal] = useState(null);
  const [form,setForm] = useState({});
  const f = k=>v=>setForm(p=>({...p,[k]:v}));
  const emptyPrest = ()=>({ nom:"",prenom:"",metier:"Ménage",tel:"",email:"",pin:"",notes:"",actif:true });
  const save = async()=>{
    if(!form.nom?.trim()) return;
    const d={...form,updatedAt:serverTimestamp()};
    if(modal==="add") await addDoc(collection(db,"prestataires"),{...d,createdAt:serverTimestamp()});
    else await updateDoc(doc(db,"prestataires",modal),d);
    setModal(null);
  };
  const del = async id=>{ if(window.confirm("Supprimer ?")) await deleteDoc(doc(db,"prestataires",id)); };
  const toggle = async p=>updateDoc(doc(db,"prestataires",p.id),{ actif:!p.actif });
  const AVC=[["#DBEAFE","#1E40AF"],["#F3E8FF","#6B21A8"],["#D1FAE5","#065F46"],["#FEF3C7","#92400E"],["#FCE7F3","#9D174D"]];
  return (
    <div>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14 }}>
        <SectionTitle>Prestataires ({prestataires.length})</SectionTitle>
        <Btn sm onClick={()=>{ setForm(emptyPrest()); setModal("add"); }}>+ Ajouter</Btn>
      </div>
      {prestataires.length===0&&<Empty icon="👷" text="Aucun prestataire."/>}
      {prestataires.map((p,i)=>{
        const [abg,ac]=AVC[i%AVC.length];
        return <Card key={p.id} accent={p.actif?T.success:T.border}>
          <div style={{ display:"flex",alignItems:"flex-start",gap:12 }}>
            <div style={{ width:36,height:36,borderRadius:"50%",background:abg,color:ac,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:13,flexShrink:0 }}>{(p.prenom?.[0]||"")}{(p.nom?.[0]||"")}</div>
            <div style={{ flex:1 }}>
              <div style={{ display:"flex",alignItems:"center",gap:7,marginBottom:3,flexWrap:"wrap" }}>
                <span style={{ fontWeight:700,fontSize:15 }}>{p.prenom} {p.nom}</span>
                <Bdg color={p.actif?T.success:T.gray} bg={p.actif?"#D1FAE5":"#F3F4F6"}>{p.actif?"Actif":"Inactif"}</Bdg>
              </div>
              {p.metier&&<div style={{ fontSize:12,fontWeight:700,color:ac,marginBottom:2 }}>🔧 {p.metier}</div>}
              <div style={{ display:"flex",gap:10,flexWrap:"wrap" }}>
                {p.tel&&<span style={{ color:T.gray,fontSize:12 }}>📞 {p.tel}</span>}
                {p.pin&&<span style={{ color:T.gray,fontSize:12 }}>🔑 {p.pin}</span>}
              </div>
            </div>
            <div style={{ display:"flex",flexDirection:"column",gap:5,alignItems:"flex-end" }}>
              <div style={{ display:"flex",gap:5 }}>
                <Btn sm outline onClick={()=>{ setForm({...p}); setModal(p.id); }}>✏️</Btn>
                <Btn sm danger onClick={()=>del(p.id)}>🗑</Btn>
              </div>
              <Btn sm outline onClick={()=>toggle(p)}>{p.actif?"Désactiver":"Activer"}</Btn>
            </div>
          </div>
        </Card>;
      })}
      {modal&&<Modal title={modal==="add"?"Nouveau prestataire":"Modifier"} onClose={()=>setModal(null)}
        footer={<><Btn sm outline onClick={()=>setModal(null)}>Annuler</Btn><Btn sm onClick={save} disabled={!form.nom?.trim()}>Enregistrer</Btn></>}>
        <G2><Input label="Prénom" value={form.prenom||""} onChange={f("prenom")}/><Input label="Nom *" value={form.nom||""} onChange={f("nom")} required/></G2>
        <Sel label="Métier" value={form.metier||"Ménage"} onChange={f("metier")} options={METIERS}/>
        <Input label="Téléphone" value={form.tel||""} onChange={f("tel")} type="tel"/>
        <Input label="Email" value={form.email||""} onChange={f("email")} type="email"/>
        <Input label="PIN (4 chiffres)" value={form.pin||""} onChange={f("pin")} placeholder="Ex : 4444"/>
        <Input label="Notes" value={form.notes||""} onChange={f("notes")}/>
      </Modal>}
    </div>
  );
}

// ─── STOCKS (gestionnaire uniquement) ────────────────────────────────────────
function Stocks({ stocks, kits, logements }) {
  const [filtre,setFiltre] = useState("kits");
  const [kitSel,setKitSel] = useState(null);
  const [modal,setModal] = useState(null);
  const [form,setForm] = useState({});
  const f = k=>v=>setForm(p=>({...p,[k]:v}));
  const kit = kits.find(k=>k.id===kitSel)||kits[0];
  const updKit = async(kitId,itemId,action,delta)=>{
    const k=kits.find(x=>x.id===kitId); if(!k) return;
    const s=k.items?.[itemId]||{e:0,p:0}; let ni={...s};
    if(action==="mettre"&&s.e>0) ni={e:s.e-1,p:s.p+1};
    else if(action==="rec"&&s.p>0) ni={e:s.e,p:s.p-1};
    else if(action==="e") ni={e:Math.max(0,s.e+delta),p:s.p};
    await updateDoc(doc(db,"kits",kitId),{[`items.${itemId}`]:ni,updatedAt:serverTimestamp()});
  };
  const updS = async(id,delta)=>{ const s=stocks.find(x=>x.id===id); if(!s) return; await updateDoc(doc(db,"stocks",id),{quantite:Math.max(0,s.quantite+delta),updatedAt:serverTimestamp()}); };
  const saveStock = async()=>{
    if(!form.nom?.trim()) return;
    const d={...form,quantite:Number(form.quantite||0),minimum:Number(form.minimum||0),updatedAt:serverTimestamp()};
    if(modal==="add") await addDoc(collection(db,"stocks"),{...d,createdAt:serverTimestamp()});
    else await updateDoc(doc(db,"stocks",modal),d);
    setModal(null);
  };
  const delS = async id=>{ if(window.confirm("Supprimer ?")) await deleteDoc(doc(db,"stocks",id)); };
  const totE=kits.reduce((a,k)=>a+KIT_ITEMS.reduce((b,i)=>b+(k.items?.[i.id]?.e||0),0),0);
  const totP=kits.reduce((a,k)=>a+KIT_ITEMS.reduce((b,i)=>b+(k.items?.[i.id]?.p||0),0),0);
  const alertes=stocks.filter(s=>s.quantite<=s.minimum);
  const lopts=[{value:"",label:"— Tous logements —"},...logements.map(l=>({value:l.nom,label:l.nom}))];
  return (
    <div>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14 }}>
        <SectionTitle>Stocks</SectionTitle>
        {filtre==="autres"&&<Btn sm onClick={()=>{ setForm({nom:"",categorie:"Hygiène",logement:"",quantite:0,minimum:0,unite:"unité"}); setModal("add"); }}>+ Ajouter</Btn>}
      </div>
      <div style={{ display:"flex",gap:8,marginBottom:14 }}>
        {[["kits","🎁 Kits bienvenue"],["autres","📦 Autres stocks"]].map(([id,lbl])=>(
          <button key={id} onClick={()=>setFiltre(id)} style={{ padding:"7px 14px",borderRadius:20,border:`1.5px solid ${filtre===id?T.purple:T.border}`,background:filtre===id?T.purple:T.white,color:filtre===id?"#fff":T.gray,fontSize:12,fontWeight:700,cursor:"pointer" }}>{lbl}</button>
        ))}
      </div>
      {filtre==="kits"&&<div>
        <Card accent={T.purple} style={{ marginBottom:14 }}>
          <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:10 }}><span>🎁</span><span style={{ fontWeight:800,color:T.purple,fontSize:14 }}>Résumé global</span></div>
          <div style={{ display:"flex",gap:10,flexWrap:"wrap" }}>
            {[{bg:"#EDE9FE",bc:"#C4B5FD",n:totE,l:"📦 Étagère",c:T.purple},{bg:"#D1FAE5",bc:"#6EE7B7",n:totP,l:"🏠 Voyageur",c:"#065F46"}].map(x=>(
              <div key={x.l} style={{ background:x.bg,border:`1px solid ${x.bc}`,borderRadius:9,padding:"8px 14px",textAlign:"center",minWidth:70 }}>
                <div style={{ fontFamily:"Raleway",fontWeight:800,fontSize:22,color:x.c }}>{x.n}</div>
                <div style={{ fontSize:10,fontWeight:700,color:x.c }}>{x.l}</div>
              </div>
            ))}
            <div style={{ flex:1,minWidth:140 }}>
              {kits.map(k=>{ const e=KIT_ITEMS.reduce((a,i)=>a+(k.items?.[i.id]?.e||0),0),p=KIT_ITEMS.reduce((a,i)=>a+(k.items?.[i.id]?.p||0),0);
              return <div key={k.id} style={{ display:"flex",justifyContent:"space-between",padding:"4px 0",borderBottom:`1px solid ${T.border}`,fontSize:12 }}>
                <span style={{ fontWeight:600 }}>📍 {k.logement}</span>
                <div style={{ display:"flex",gap:5 }}><Bdg color={T.purple} bg="#EDE9FE">📦 {e}</Bdg><Bdg color="#065F46" bg="#D1FAE5">🏠 {p}</Bdg></div>
              </div>; })}
            </div>
          </div>
        </Card>
        <div style={{ display:"flex",gap:6,marginBottom:12,flexWrap:"wrap" }}>
          {kits.map(k=><button key={k.id} onClick={()=>setKitSel(k.id)}
            style={{ padding:"6px 12px",borderRadius:20,border:`1.5px solid ${(kitSel||kits[0]?.id)===k.id?T.purple:T.border}`,background:(kitSel||kits[0]?.id)===k.id?T.purple:T.white,color:(kitSel||kits[0]?.id)===k.id?"#fff":T.gray,fontSize:11,fontWeight:700,cursor:"pointer" }}>
            📍 {k.logement}
          </button>)}
        </div>
        {kit&&<Card accent={T.purple}>
          <div style={{ fontWeight:800,color:T.purple,marginBottom:10,fontSize:13 }}>📍 {kit.logement}</div>
          {KIT_ITEMS.map(item=>{ const v=kit.items?.[item.id]||{e:0,p:0}; const al=v.e===0;
          return <div key={item.id} style={{ display:"grid",gridTemplateColumns:"1fr 80px 64px auto",gap:8,alignItems:"center",padding:"9px 10px",borderRadius:9,background:al?"#FEF2F2":"#F8FAFC",marginBottom:5,border:`1px solid ${al?"#FECACA":T.border}`,borderLeft:`3px solid ${al?T.danger:T.purple}` }}>
            <div style={{ display:"flex",alignItems:"center",gap:7 }}>
              <span style={{ fontSize:17 }}>{item.icon}</span>
              <div><div style={{ fontWeight:600,fontSize:12 }}>{item.nom}</div><div style={{ fontSize:10,color:T.gray2 }}>{item.unite}</div></div>
              {al&&<Bdg color={T.danger} bg="#FEE2E2">Rupture</Bdg>}
            </div>
            <div style={{ background:"#EDE9FE",border:"1px solid #C4B5FD",borderRadius:8,padding:"5px 8px",textAlign:"center" }}>
              <div style={{ display:"flex",alignItems:"center",gap:3 }}>
                <button onClick={()=>updKit(kit.id,item.id,"e",-1)} style={{ width:20,height:20,borderRadius:"50%",border:`1px solid ${T.border}`,background:T.white,cursor:"pointer",fontSize:13,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center" }}>−</button>
                <span style={{ fontWeight:800,color:T.purple,minWidth:20,textAlign:"center",fontSize:16 }}>{v.e}</span>
                <button onClick={()=>updKit(kit.id,item.id,"e",+1)} style={{ width:20,height:20,borderRadius:"50%",border:`1px solid ${T.border}`,background:T.white,cursor:"pointer",fontSize:13,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center" }}>+</button>
              </div>
              <div style={{ fontSize:9,fontWeight:700,color:T.purple,marginTop:2 }}>📦 Étagère</div>
            </div>
            <div style={{ background:"#D1FAE5",border:"1px solid #6EE7B7",borderRadius:8,padding:"5px 8px",textAlign:"center" }}>
              <div style={{ fontWeight:800,color:"#065F46",fontSize:16 }}>{v.p}</div>
              <div style={{ fontSize:9,fontWeight:700,color:"#065F46",marginTop:2 }}>🏠 Voyageur</div>
            </div>
            <div style={{ display:"flex",flexDirection:"column",gap:4 }}>
              <button onClick={()=>updKit(kit.id,item.id,"mettre")} disabled={v.e<=0} style={{ padding:"4px 7px",borderRadius:7,border:"none",fontSize:10,fontWeight:700,cursor:v.e<=0?"not-allowed":"pointer",background:v.e<=0?"#F3F4F6":"#D1FAE5",color:v.e<=0?"#9CA3AF":"#065F46" }}>▶ Mettre</button>
              <button onClick={()=>updKit(kit.id,item.id,"rec")} disabled={v.p<=0} style={{ padding:"4px 7px",borderRadius:7,border:"none",fontSize:10,fontWeight:700,cursor:v.p<=0?"not-allowed":"pointer",background:v.p<=0?"#F3F4F6":"#FEF3C7",color:v.p<=0?"#9CA3AF":"#92400E" }}>↩ Récupéré</button>
            </div>
          </div>; })}
        </Card>}
      </div>}
      {filtre==="autres"&&<div>
        {alertes.length>0&&<div style={{ background:"#FEF3C7",border:"1px solid #FCD34D",borderRadius:10,padding:"9px 13px",marginBottom:12,fontSize:12,fontWeight:700,color:"#92400E" }}>⚠️ {alertes.length} produit(s) en niveau critique</div>}
        {stocks.length===0&&<Empty icon="📦" text="Aucun produit. Cliquez sur + Ajouter."/>}
        {stocks.map(s=>{ const al=s.quantite<=s.minimum;
        return <Card key={s.id} accent={al?T.danger:T.success}>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",gap:10 }}>
            <div style={{ flex:1 }}>
              <div style={{ display:"flex",alignItems:"center",gap:6,marginBottom:2,flexWrap:"wrap" }}>
                <span style={{ fontWeight:700 }}>{s.nom}</span>
                {al&&<Bdg color={T.danger} bg="#FEE2E2">Rupture</Bdg>}
                {s.categorie&&<Bdg color="#0369A1" bg="#E0F2FE">{s.categorie}</Bdg>}
              </div>
              <div style={{ color:T.gray,fontSize:11 }}>📍 {s.logement} · Min : {s.minimum} {s.unite}</div>
            </div>
            <div style={{ display:"flex",alignItems:"center",gap:6 }}>
              {[-1,null,+1].map((d,i)=>d===null
                ?<span key="n" style={{ fontWeight:800,fontSize:20,minWidth:28,textAlign:"center",color:al?T.danger:T.success }}>{s.quantite}</span>
                :<button key={i} onClick={()=>updS(s.id,d)} style={{ width:26,height:26,borderRadius:"50%",border:`1.5px solid ${T.border}`,background:T.white,cursor:"pointer",fontSize:15,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center" }}>{d>0?"+":"−"}</button>
              )}
              <span style={{ color:T.gray2,fontSize:11 }}>{s.unite}</span>
              <Btn sm outline onClick={()=>{ setForm({...s}); setModal(s.id); }}>✏️</Btn>
              <Btn sm danger onClick={()=>delS(s.id)}>🗑</Btn>
            </div>
          </div>
        </Card>; })}
        {modal&&<Modal title={modal==="add"?"Nouveau produit":"Modifier"} onClose={()=>setModal(null)}
          footer={<><Btn sm outline onClick={()=>setModal(null)}>Annuler</Btn><Btn sm onClick={saveStock} disabled={!form.nom?.trim()}>Enregistrer</Btn></>}>
          <Input label="Nom *" value={form.nom||""} onChange={f("nom")} required/>
          <Sel label="Logement" value={form.logement||""} onChange={f("logement")} options={lopts}/>
          <Input label="Catégorie" value={form.categorie||""} onChange={f("categorie")} placeholder="Hygiène, Linge..."/>
          <Sel label="Unité" value={form.unite||"unité"} onChange={f("unite")} options={["unité","rouleau","flacon","sachet","litre","kg","paire","sac"]}/>
          <G2><Input label="Quantité" value={form.quantite||0} onChange={f("quantite")} type="number"/><Input label="Minimum (alerte)" value={form.minimum||0} onChange={f("minimum")} type="number"/></G2>
        </Modal>}
      </div>}
    </div>
  );
}

// ─── PPL ──────────────────────────────────────────────────────────────────────
const PPL_STOCK_ITEMS = [
  { id:"serviettes",nom:"Serviettes bain",icon:"🛁" },{ id:"draps",nom:"Draps",icon:"🛏" },
  { id:"savon",nom:"Savon",icon:"🧼" },{ id:"shampoing",nom:"Shampoing",icon:"🧴" },
  { id:"papierWC",nom:"Papier toilette",icon:"🧻" },{ id:"prodSol",nom:"Produit sol",icon:"🫧" },
  { id:"sacs",nom:"Sacs poubelle",icon:"🗑" },{ id:"eponges",nom:"Éponges",icon:"🧽" },
];
function PPL({ ppls, logements, prestataires, user }) {
  const [modal,setModal] = useState(null);
  const [form,setForm] = useState({});
  const [stockForm,setStockForm] = useState({});
  const [view,setView] = useState("actifs");
  const f = k=>v=>setForm(p=>({...p,[k]:v}));
  const emptyPPL = ()=>({ titre:"",logement:"",type:"Ménage",date:"",heure:"",duree:"",priorite:"normale",notes:"",nbVoyageurs:0,nomVoyageur:"",reservationId:"" });
  const openAdd = ()=>{ setForm(emptyPPL()); setStockForm({}); setModal("add"); };
  const openEdit = p=>{ setForm({...p}); setStockForm(p.stocksNecessaires||{}); setModal(p.id); };
  const save = async()=>{
    if(!form.titre?.trim()) return;
    const d={...form,nbVoyageurs:Number(form.nbVoyageurs||0),stocksNecessaires:stockForm,updatedAt:serverTimestamp()};
    if(modal==="add") await addDoc(collection(db,"ppls"),{...d,statut:"disponible",candidats:[],prestataire:null,createdAt:serverTimestamp()});
    else await updateDoc(doc(db,"ppls",modal),d);
    setModal(null);
  };
  const del = async id=>{ if(window.confirm("Supprimer ?")) await deleteDoc(doc(db,"ppls",id)); };
  const cloturer = async id=>updateDoc(doc(db,"ppls",id),{statut:"clôturé",updatedAt:serverTimestamp()});
  const rouvrir = async id=>updateDoc(doc(db,"ppls",id),{statut:"disponible",updatedAt:serverTimestamp()});
  const postuler = async ppl=>{ if(ppl.candidats?.includes(user.name)) return; await updateDoc(doc(db,"ppls",ppl.id),{candidats:[...(ppl.candidats||[]),user.name],updatedAt:serverTimestamp()}); };
  const assigner = async(ppl,nom)=>updateDoc(doc(db,"ppls",ppl.id),{prestataire:nom,statut:"assigné",updatedAt:serverTimestamp()});
  const popts=[{value:"",label:"— Assigner —"},...prestataires.filter(p=>p.actif).map(p=>({value:`${p.prenom} ${p.nom}`,label:`${p.prenom} ${p.nom}`}))];
  const lopts=[{value:"",label:"— Choisir —"},...logements.map(l=>({value:l.nom,label:l.nom}))];
  const PC={normale:{bg:"#E0F2FE",c:"#0369A1"},haute:{bg:"#FEF3C7",c:"#92400E"},urgente:{bg:"#FEE2E2",c:"#991B1B"}};
  const SC={disponible:{bg:T.tealLight,c:T.teal},assigné:{bg:"#D1FAE5",c:"#065F46"},"clôturé":{bg:"#F3F4F6",c:T.gray}};
  const PL={normale:T.teal,haute:T.warn,urgente:T.danger};
  const all = user.role==="manager"?ppls:ppls.filter(p=>p.statut!=="clôturé");
  const filtered = view==="actifs"?all.filter(p=>p.statut!=="clôturé"):all.filter(p=>p.statut==="clôturé");
  return (
    <div>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14 }}>
        <SectionTitle>PPL ({filtered.length})</SectionTitle>
        {user.role==="manager"&&<Btn sm onClick={openAdd}>+ Créer PPL</Btn>}
      </div>
      {user.role==="manager"&&<div style={{ display:"flex",gap:6,marginBottom:12 }}>
        {[["actifs","Actifs"],["clotures","Clôturés"]].map(([id,lbl])=>(
          <button key={id} onClick={()=>setView(id)} style={{ padding:"6px 14px",borderRadius:20,border:`1.5px solid ${view===id?T.navy:T.border}`,background:view===id?T.navy:T.white,color:view===id?"#fff":T.gray,fontSize:12,fontWeight:700,cursor:"pointer" }}>{lbl}</button>
        ))}
      </div>}
      {filtered.length===0&&<Empty icon="📋" text="Aucune prestation."/>}
      {filtered.map(p=>(
        <Card key={p.id} accent={PL[p.priorite]||T.teal}>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,marginBottom:6 }}>
            <div style={{ flex:1 }}>
              <div style={{ display:"flex",alignItems:"center",gap:6,marginBottom:4,flexWrap:"wrap" }}>
                <span style={{ fontWeight:700,fontSize:15 }}>{p.titre}</span>
                <Bdg color={SC[p.statut]?.c||T.teal} bg={SC[p.statut]?.bg}>{p.statut}</Bdg>
                {p.priorite!=="normale"&&<Bdg color={PC[p.priorite]?.c} bg={PC[p.priorite]?.bg}>{p.priorite}</Bdg>}
                {p.reservationId&&<Bdg color="#6B21A8" bg="#F3E8FF">Lodgify #{p.reservationId}</Bdg>}
              </div>
              <div style={{ color:T.gray,fontSize:12,display:"flex",gap:8,flexWrap:"wrap" }}>
                {p.logement&&<span>📍 {p.logement}</span>}
                <span>🔧 {p.type}</span>
                {p.date&&<span>📅 {p.date}{p.heure&&" "+p.heure}</span>}
                {p.duree&&<span>⏱ {p.duree}</span>}
              </div>
              {p.nbVoyageurs>0&&<div style={{ marginTop:4,display:"inline-flex",alignItems:"center",gap:4,background:"#EDE9FE",borderRadius:7,padding:"3px 9px",fontSize:12,fontWeight:700,color:T.purple }}>
                👥 {p.nbVoyageurs} voyageur{p.nbVoyageurs>1?"s":""}{p.nomVoyageur&&` — ${p.nomVoyageur}`}
              </div>}
              {p.prestataire&&<div style={{ color:T.success,fontSize:12,marginTop:4,fontWeight:600 }}>👤 Assigné : {p.prestataire}</div>}
              {p.candidats?.length>0&&user.role==="manager"&&<div style={{ color:T.warn,fontSize:12,marginTop:3 }}>🙋 Candidat(s) : {p.candidats.join(", ")}</div>}
              {p.notes&&<div style={{ color:T.gray2,fontSize:11,marginTop:4,fontStyle:"italic" }}>{p.notes}</div>}
            </div>
            {user.role==="manager"&&<div style={{ display:"flex",gap:5,flexDirection:"column",alignItems:"flex-end",flexShrink:0 }}>
              <div style={{ display:"flex",gap:5 }}>
                <Btn sm outline onClick={()=>openEdit(p)}>✏️</Btn>
                <Btn sm danger onClick={()=>del(p.id)}>🗑</Btn>
              </div>
              {p.statut!=="clôturé"?<Btn sm outline onClick={()=>cloturer(p.id)}>Clôturer</Btn>:<Btn sm outline onClick={()=>rouvrir(p.id)}>Rouvrir</Btn>}
            </div>}
          </div>
          {user.role==="manager"&&p.candidats?.length>0&&p.statut==="disponible"&&(
            <div style={{ background:T.bg,borderRadius:8,padding:"7px 10px",marginTop:6,display:"flex",alignItems:"center",gap:8 }}>
              <span style={{ fontSize:12,color:T.gray,fontWeight:600,flexShrink:0 }}>Assigner à :</span>
              <select style={{ flex:1,padding:"5px 8px",fontSize:12,border:`1px solid ${T.border}`,borderRadius:7,background:T.white }} value="" onChange={e=>{ if(e.target.value) assigner(p,e.target.value); }}>
                {popts.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          )}
          {user.role==="prestataire"&&p.statut==="disponible"&&(
            <button onClick={()=>postuler(p)} disabled={p.candidats?.includes(user.name)}
              style={{ marginTop:8,padding:"7px 14px",borderRadius:9,border:"none",fontSize:12,fontWeight:700,cursor:p.candidats?.includes(user.name)?"default":"pointer",background:p.candidats?.includes(user.name)?"#D1FAE5":T.success,color:p.candidats?.includes(user.name)?"#065F46":"#fff" }}>
              {p.candidats?.includes(user.name)?"✅ Candidature envoyée":"🙋 Je me propose"}
            </button>
          )}
          {user.role==="prestataire"&&p.statut==="assigné"&&p.prestataire===user.name&&p.stocksNecessaires&&Object.keys(p.stocksNecessaires).length>0&&(
            <div style={{ marginTop:8,padding:"8px 10px",background:T.tealLight,borderRadius:9,border:`1px solid ${T.border}` }}>
              <div style={{ fontSize:11,fontWeight:700,color:T.navy,marginBottom:6 }}>📦 Matériel nécessaire :</div>
              <div style={{ display:"flex",flexWrap:"wrap",gap:6 }}>
                {Object.entries(p.stocksNecessaires).filter(([,v])=>v>0).map(([k,v])=>{
                  const item=PPL_STOCK_ITEMS.find(i=>i.id===k);
                  return <span key={k} style={{ background:T.white,border:`1px solid ${T.border}`,borderRadius:7,padding:"3px 8px",fontSize:11,fontWeight:600 }}>{item?.icon||"•"} {item?.nom||k} × {v}</span>;
                })}
              </div>
            </div>
          )}
        </Card>
      ))}
      {modal&&user.role==="manager"&&<Modal title={modal==="add"?"Nouveau PPL":"Modifier PPL"} onClose={()=>setModal(null)} wide
        footer={<><Btn sm outline onClick={()=>setModal(null)}>Annuler</Btn><Btn sm onClick={save} disabled={!form.titre?.trim()}>Enregistrer</Btn></>}>
        <Input label="Titre *" value={form.titre||""} onChange={f("titre")} placeholder="Ex : Ménage Suite Yellow" required/>
        <Sel label="Logement" value={form.logement||""} onChange={f("logement")} options={lopts}/>
        <Sel label="Type" value={form.type||"Ménage"} onChange={f("type")} options={TYPES_PPL}/>
        <Sel label="Priorité" value={form.priorite||"normale"} onChange={f("priorite")} options={PRIOS}/>
        <G2><Input label="Date" value={form.date||""} onChange={f("date")} type="date"/><Input label="Heure" value={form.heure||""} onChange={f("heure")} type="time"/></G2>
        <G2><Input label="Durée estimée" value={form.duree||""} onChange={f("duree")} placeholder="Ex : 2h"/><Input label="Nb voyageurs suivants" value={form.nbVoyageurs||0} onChange={f("nbVoyageurs")} type="number" min="0"/></G2>
        <Input label="Nom voyageur" value={form.nomVoyageur||""} onChange={f("nomVoyageur")} placeholder="Ex : Dupont"/>
        <Input label="ID réservation Lodgify" value={form.reservationId||""} onChange={f("reservationId")} placeholder="Auto-rempli via webhook"/>
        <Input label="Notes" value={form.notes||""} onChange={f("notes")}/>
        <div style={{ marginTop:12,paddingTop:12,borderTop:`1px solid ${T.border}` }}>
          <H3>📦 Matériel nécessaire (visible par le prestataire)</H3>
          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8 }}>
            {PPL_STOCK_ITEMS.map(item=>(
              <div key={item.id} style={{ display:"flex",alignItems:"center",gap:6,background:T.bg,borderRadius:8,padding:"6px 10px" }}>
                <span style={{ fontSize:15 }}>{item.icon}</span>
                <span style={{ fontSize:12,fontWeight:600,flex:1 }}>{item.nom}</span>
                <input type="number" min="0" value={stockForm[item.id]||0} onChange={e=>setStockForm(s=>({...s,[item.id]:Number(e.target.value)}))}
                  style={{ width:48,padding:"4px 6px",border:`1px solid ${T.border}`,borderRadius:7,fontSize:12,textAlign:"center" }}/>
              </div>
            ))}
          </div>
        </div>
      </Modal>}
    </div>
  );
}

// ─── TÂCHES ───────────────────────────────────────────────────────────────────
function Taches({ taches, logements, prestataires, user }) {
  const [modal,setModal] = useState(null);
  const [form,setForm] = useState({});
  const f = k=>v=>setForm(p=>({...p,[k]:v}));
  const save = async()=>{
    if(!form.titre?.trim()) return;
    const d={...form,updatedAt:serverTimestamp()};
    if(modal==="add") await addDoc(collection(db,"taches"),{...d,statut:"à faire",createdAt:serverTimestamp()});
    else await updateDoc(doc(db,"taches",modal),d);
    setModal(null);
  };
  const del = async id=>{ if(window.confirm("Supprimer ?")) await deleteDoc(doc(db,"taches",id)); };
  const setStatut = async(id,s)=>updateDoc(doc(db,"taches",id),{statut:s,updatedAt:serverTimestamp()});
  const list = user.role==="prestataire"?taches.filter(t=>t.assignea===user.name):taches;
  const lopts=[{value:"",label:"— Tous —"},...logements.map(l=>({value:l.nom,label:l.nom}))];
  const popts=[{value:"",label:"— Non assigné —"},...prestataires.filter(p=>p.actif).map(p=>({value:`${p.prenom} ${p.nom}`,label:`${p.prenom} ${p.nom}`}))];
  const PL={normale:T.teal,haute:T.warn,urgente:T.danger};
  const SC={"à faire":{bg:"#FEF3C7",c:"#92400E"},"en cours":{bg:"#DBEAFE",c:"#1E40AF"},terminé:{bg:"#D1FAE5",c:"#065F46"}};
  return (
    <div>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14 }}>
        <SectionTitle>Tâches ({list.length})</SectionTitle>
        {user.role==="manager"&&<Btn sm onClick={()=>{ setForm({titre:"",description:"",logement:"",assignea:"",echeance:"",priorite:"normale"}); setModal("add"); }}>+ Ajouter</Btn>}
      </div>
      {list.length===0&&<Empty icon="✅" text="Aucune tâche."/>}
      {list.map(t=>(
        <Card key={t.id} accent={PL[t.priorite]||T.teal}>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8 }}>
            <div style={{ flex:1 }}>
              <div style={{ display:"flex",alignItems:"center",gap:6,marginBottom:4,flexWrap:"wrap" }}>
                <span style={{ fontWeight:700 }}>{t.titre}</span>
                <Bdg color={SC[t.statut]?.c} bg={SC[t.statut]?.bg}>{t.statut}</Bdg>
                {t.priorite!=="normale"&&<Bdg color={PL[t.priorite]} bg={PL[t.priorite]+"22"}>{t.priorite}</Bdg>}
              </div>
              {t.description&&<div style={{ color:T.gray,fontSize:12,marginBottom:2 }}>{t.description}</div>}
              <div style={{ color:T.gray2,fontSize:11,display:"flex",gap:8,flexWrap:"wrap" }}>
                {t.logement&&<span>📍 {t.logement}</span>}
                {t.assignea&&<span>👤 {t.assignea}</span>}
                {t.echeance&&<span>📅 {t.echeance}</span>}
              </div>
            </div>
            <div style={{ display:"flex",flexDirection:"column",gap:5,alignItems:"flex-end",flexShrink:0 }}>
              {user.role==="manager"&&<div style={{ display:"flex",gap:4 }}>
                <Btn sm outline onClick={()=>{ setForm({...t}); setModal(t.id); }}>✏️</Btn>
                <Btn sm danger onClick={()=>del(t.id)}>🗑</Btn>
              </div>}
              <div style={{ display:"flex",gap:4,flexWrap:"wrap",justifyContent:"flex-end" }}>
                {t.statut==="à faire"&&<button onClick={()=>setStatut(t.id,"en cours")} style={{ padding:"4px 9px",borderRadius:8,border:"none",fontSize:11,fontWeight:700,background:"#DBEAFE",color:"#1E40AF",cursor:"pointer" }}>▶ En cours</button>}
                {t.statut!=="terminé"&&<button onClick={()=>setStatut(t.id,"terminé")} style={{ padding:"4px 9px",borderRadius:8,border:"none",fontSize:11,fontWeight:700,background:"#D1FAE5",color:"#065F46",cursor:"pointer" }}>✓ Terminé</button>}
                {t.statut==="terminé"&&user.role==="manager"&&<Btn sm outline onClick={()=>setStatut(t.id,"à faire")}>↩</Btn>}
              </div>
            </div>
          </div>
        </Card>
      ))}
      {modal&&<Modal title={modal==="add"?"Nouvelle tâche":"Modifier la tâche"} onClose={()=>setModal(null)}
        footer={<><Btn sm outline onClick={()=>setModal(null)}>Annuler</Btn><Btn sm onClick={save} disabled={!form.titre?.trim()}>Enregistrer</Btn></>}>
        <Input label="Titre *" value={form.titre||""} onChange={f("titre")} required/>
        <Input label="Description" value={form.description||""} onChange={f("description")}/>
        <Sel label="Logement" value={form.logement||""} onChange={f("logement")} options={lopts}/>
        <Sel label="Assigner à" value={form.assignea||""} onChange={f("assignea")} options={popts}/>
        <Sel label="Priorité" value={form.priorite||"normale"} onChange={f("priorite")} options={PRIOS}/>
        <Input label="Échéance" value={form.echeance||""} onChange={f("echeance")} type="date"/>
      </Modal>}
    </div>
  );
}

// ─── CALENDRIER GESTIONNAIRE ──────────────────────────────────────────────────
const EV_COLORS = {
  "Vérification logement":{ bg:"#D1FAE5",c:"#065F46",dot:"#0EA572" },
  "Maintenance / réparation":{ bg:"#FEE2E2",c:"#991B1B",dot:T.danger },
  "RDV personnel":{ bg:"#F3E8FF",c:"#6B21A8",dot:T.purple },
  "Service additionnel":{ bg:"#DBEAFE",c:"#1E40AF",dot:T.teal },
  "PPL":{ bg:"#FEF3C7",c:"#92400E",dot:T.warn },
};
function Calendrier({ evenements, ppls, logements }) {
  const now = new Date();
  const [year,setYear] = useState(now.getFullYear());
  const [month,setMonth] = useState(now.getMonth());
  const [modal,setModal] = useState(null);
  const [selDay,setSelDay] = useState(null);
  const [form,setForm] = useState({});
  const f = k=>v=>setForm(p=>({...p,[k]:v}));
  const prev=()=>{ if(month===0){setMonth(11);setYear(y=>y-1);}else setMonth(m=>m-1); setSelDay(null); };
  const next=()=>{ if(month===11){setMonth(0);setYear(y=>y+1);}else setMonth(m=>m+1); setSelDay(null); };
  const days = (() => {
    const first=new Date(year,month,1), last=new Date(year,month+1,0);
    const startDow=(first.getDay()+6)%7, cells=[];
    for(let i=0;i<startDow;i++){const d=new Date(year,month,1-startDow+i);cells.push({date:d,cur:false});}
    for(let d=1;d<=last.getDate();d++) cells.push({date:new Date(year,month,d),cur:true});
    while(cells.length%7!==0){const l=cells[cells.length-1].date,nd=new Date(l);nd.setDate(nd.getDate()+1);cells.push({date:nd,cur:false});}
    return cells;
  })();
  const toStr=d=>`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  const todayStr=toStr(now);
  const allEvs = [...evenements.map(e=>({...e,_type:"ev"})),...ppls.filter(p=>p.statut!=="clôturé"&&p.date).map(p=>({id:p.id,titre:p.titre,date:p.date,heure:p.heure||"",type:"PPL",logement:p.logement,nbVoyageurs:p.nbVoyageurs,_type:"ppl"}))];
  const evOnDay=d=>{const s=toStr(d);return allEvs.filter(e=>e.date===s);};
  const selEvs=selDay?evOnDay(selDay):[];
  const save=async()=>{
    if(!form.titre?.trim()) return;
    const d={...form,updatedAt:serverTimestamp()};
    if(modal==="add") await addDoc(collection(db,"evenements"),{...d,createdAt:serverTimestamp()});
    else await updateDoc(doc(db,"evenements",modal),d);
    setModal(null);
  };
  const del=async id=>{if(window.confirm("Supprimer ?")) await deleteDoc(doc(db,"evenements",id)); setSelDay(null);};
  const lopts=[{value:"",label:"— Tous logements —"},...logements.map(l=>({value:l.nom,label:l.nom}))];
  return (
    <div>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14 }}>
        <SectionTitle>Calendrier</SectionTitle>
        <Btn sm onClick={()=>{ setForm({titre:"",type:"Vérification logement",logement:"",date:todayStr,heure:"",notes:""}); setModal("add"); }}>+ Événement</Btn>
      </div>
      <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12 }}>
        <button onClick={prev} style={{ width:34,height:34,borderRadius:"50%",border:`1.5px solid ${T.border}`,background:T.white,cursor:"pointer",fontSize:18,display:"flex",alignItems:"center",justifyContent:"center",color:T.gray }}>‹</button>
        <div style={{ fontFamily:"Raleway",fontWeight:800,fontSize:17,color:T.navy }}>{MONTHS[month]} {year}</div>
        <button onClick={next} style={{ width:34,height:34,borderRadius:"50%",border:`1.5px solid ${T.border}`,background:T.white,cursor:"pointer",fontSize:18,display:"flex",alignItems:"center",justifyContent:"center",color:T.gray }}>›</button>
      </div>
      {/* Légende */}
      <div style={{ display:"flex",gap:8,flexWrap:"wrap",marginBottom:12 }}>
        {Object.entries(EV_COLORS).map(([type,c])=>(
          <div key={type} style={{ display:"flex",alignItems:"center",gap:4,fontSize:11,color:T.gray }}>
            <div style={{ width:8,height:8,borderRadius:"50%",background:c.dot,flexShrink:0 }}/>
            <span>{type}</span>
          </div>
        ))}
      </div>
      {/* Grille */}
      <div style={{ display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3 }}>
        {DAYS_SHORT.map(d=><div key={d} style={{ textAlign:"center",fontSize:11,fontWeight:700,color:T.gray2,padding:"5px 0" }}>{d}</div>)}
        {days.map((cell,i)=>{
          const evs=evOnDay(cell.date);
          const isSel=selDay&&selDay.toDateString()===cell.date.toDateString();
          const isToday=toStr(cell.date)===todayStr;
          return <div key={i} onClick={()=>setSelDay(cell.date)}
            style={{ minHeight:64,borderRadius:9,padding:"4px 3px",border:`1.5px solid ${isSel?"#4BA3C3":isToday?"#4BA3C3":T.border}`,background:isSel?"#E0F7FF":isToday?"#F0F9FF":cell.cur?T.white:"#F8FAFC",opacity:cell.cur?1:.5,cursor:"pointer",transition:"all .1s" }}>
            <div style={{ fontSize:12,fontWeight:700,color:isToday?T.teal:T.gray,marginBottom:2,paddingLeft:2 }}>{cell.date.getDate()}</div>
            {evs.slice(0,2).map(e=>{ const ec=EV_COLORS[e.type]||EV_COLORS["PPL"];
              return <div key={e.id} style={{ background:ec.bg,color:ec.c,borderRadius:4,padding:"1px 4px",fontSize:9,fontWeight:700,marginBottom:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis" }}>
                {e.heure?e.heure+" ":""}{e.titre}
              </div>;
            })}
            {evs.length>2&&<div style={{ fontSize:9,color:T.gray2,fontWeight:700,paddingLeft:2 }}>+{evs.length-2}</div>}
          </div>;
        })}
      </div>
      {/* Détail jour */}
      {selDay&&selEvs.length>0&&<div style={{ background:T.white,borderRadius:12,border:`1px solid ${T.border}`,padding:14,marginTop:12 }}>
        <H3>{selDay.toLocaleDateString("fr-FR",{weekday:"long",day:"numeric",month:"long"})}</H3>
        {selEvs.map(e=>{ const ec=EV_COLORS[e.type]||EV_COLORS["PPL"];
          return <div key={e.id} style={{ background:ec.bg,border:`1px solid ${ec.dot}22`,borderRadius:9,padding:"10px 12px",marginBottom:8 }}>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start" }}>
              <div>
                <div style={{ fontWeight:700,color:ec.c,marginBottom:3 }}>{e.titre}</div>
                <div style={{ fontSize:12,color:T.gray,display:"flex",gap:8,flexWrap:"wrap" }}>
                  {e.logement&&<span>📍 {e.logement}</span>}
                  {e.heure&&<span>🕐 {e.heure}</span>}
                  {e.nbVoyageurs>0&&<span>👥 {e.nbVoyageurs} voy.</span>}
                </div>
                {e.notes&&<div style={{ color:T.gray2,fontSize:11,marginTop:4,fontStyle:"italic" }}>{e.notes}</div>}
              </div>
              {e._type==="ev"&&<Btn sm danger onClick={()=>del(e.id)}>🗑</Btn>}
            </div>
          </div>;
        })}
      </div>}
      {modal&&<Modal title={modal==="add"?"Nouvel événement":"Modifier"} onClose={()=>setModal(null)}
        footer={<><Btn sm outline onClick={()=>setModal(null)}>Annuler</Btn><Btn sm onClick={save} disabled={!form.titre?.trim()}>Enregistrer</Btn></>}>
        <Input label="Titre *" value={form.titre||""} onChange={f("titre")} required/>
        <Sel label="Type" value={form.type||"Vérification logement"} onChange={f("type")} options={TYPES_CAL}/>
        <Sel label="Logement" value={form.logement||""} onChange={f("logement")} options={lopts}/>
        <G2><Input label="Date" value={form.date||""} onChange={f("date")} type="date"/><Input label="Heure" value={form.heure||""} onChange={f("heure")} type="time"/></G2>
        <Input label="Notes" value={form.notes||""} onChange={f("notes")}/>
      </Modal>}
    </div>
  );
}

// ─── SERVICES ADDITIONNELS ────────────────────────────────────────────────────
function Services({ commandes, logements, services }) {
  const [modal,setModal] = useState(null);
  const [form,setForm] = useState({});
  const f = k=>v=>setForm(p=>({...p,[k]:v}));
  const [svcModal,setSvcModal] = useState(false);
  const [newSvc,setNewSvc] = useState({ nom:"",icon:"🛎",prix:0,desc:"" });
  const allServices = [...SERVICES_BASE,...(services||[])];
  const lopts=[{value:"",label:"— Choisir —"},...logements.map(l=>({value:l.nom,label:l.nom}))];
  const save=async()=>{
    if(!form.service||!form.logement) return;
    const d={...form,statut:"en_attente",updatedAt:serverTimestamp()};
    if(modal==="add") await addDoc(collection(db,"commandes"),{...d,createdAt:serverTimestamp()});
    else await updateDoc(doc(db,"commandes",modal),d);
    setModal(null);
  };
  const setStatut=async(id,s)=>updateDoc(doc(db,"commandes",id),{statut:s,updatedAt:serverTimestamp()});
  const del=async id=>{if(window.confirm("Supprimer ?")) await deleteDoc(doc(db,"commandes",id));};
  const addSvc=async()=>{ if(!newSvc.nom.trim()) return; await addDoc(collection(db,"services"),{...newSvc,createdAt:serverTimestamp()}); setSvcModal(false); setNewSvc({nom:"",icon:"🛎",prix:0,desc:""}); };
  const SC_COLORS={ en_attente:{bg:"#FEF3C7",c:"#92400E"},confirmé:{bg:"#D1FAE5",c:"#065F46"},annulé:{bg:"#F3F4F6",c:T.gray} };
  return (
    <div>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14 }}>
        <SectionTitle>Services additionnels</SectionTitle>
        <div style={{ display:"flex",gap:6 }}>
          <Btn sm outline onClick={()=>setSvcModal(true)}>+ Service</Btn>
          <Btn sm onClick={()=>{ setForm({service:"",icon:"",logement:"",nomVoyageur:"",dateArrivee:"",notes:""}); setModal("add"); }}>+ Commande</Btn>
        </div>
      </div>
      {/* Catalogue */}
      <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:8,marginBottom:16 }}>
        {allServices.map(s=>(
          <div key={s.id} style={{ background:T.white,borderRadius:10,padding:"10px 12px",border:`1px solid ${T.border}`,cursor:"pointer",transition:"all .15s" }}
            onClick={()=>{ setForm({ service:s.nom,icon:s.icon,logement:"",nomVoyageur:"",dateArrivee:"",notes:"" }); setModal("add"); }}>
            <div style={{ fontSize:22,marginBottom:4 }}>{s.icon}</div>
            <div style={{ fontWeight:700,fontSize:12,marginBottom:2 }}>{s.nom}</div>
            <div style={{ color:T.teal,fontSize:12,fontWeight:700 }}>{s.prix}€</div>
            <div style={{ color:T.gray2,fontSize:10,marginTop:2 }}>{s.desc}</div>
          </div>
        ))}
      </div>
      {/* Commandes */}
      <H3>Commandes en cours ({commandes.length})</H3>
      {commandes.length===0&&<Empty icon="🛎" text="Aucune commande."/>}
      {commandes.map(c=>(
        <Card key={c.id} accent={T.teal}>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8 }}>
            <div>
              <div style={{ display:"flex",alignItems:"center",gap:6,marginBottom:4,flexWrap:"wrap" }}>
                <span style={{ fontSize:16 }}>{c.icon}</span>
                <span style={{ fontWeight:700 }}>{c.service}</span>
                <Bdg color={SC_COLORS[c.statut]?.c} bg={SC_COLORS[c.statut]?.bg}>{c.statut?.replace("_"," ")}</Bdg>
              </div>
              <div style={{ color:T.gray,fontSize:12,display:"flex",gap:8,flexWrap:"wrap" }}>
                {c.logement&&<span>📍 {c.logement}</span>}
                {c.nomVoyageur&&<span>👤 {c.nomVoyageur}</span>}
                {c.dateArrivee&&<span>📅 {c.dateArrivee}</span>}
              </div>
              {c.notes&&<div style={{ color:T.gray2,fontSize:11,marginTop:4,fontStyle:"italic" }}>{c.notes}</div>}
            </div>
            <div style={{ display:"flex",gap:5,flexDirection:"column",alignItems:"flex-end" }}>
              <div style={{ display:"flex",gap:4 }}>
                {c.statut==="en_attente"&&<button onClick={()=>setStatut(c.id,"confirmé")} style={{ padding:"4px 9px",borderRadius:8,border:"none",fontSize:11,fontWeight:700,background:"#D1FAE5",color:"#065F46",cursor:"pointer" }}>✓ Confirmer</button>}
                {c.statut==="en_attente"&&<button onClick={()=>setStatut(c.id,"annulé")} style={{ padding:"4px 9px",borderRadius:8,border:"none",fontSize:11,fontWeight:700,background:"#FEE2E2",color:"#991B1B",cursor:"pointer" }}>✕ Annuler</button>}
                <Btn sm danger onClick={()=>del(c.id)}>🗑</Btn>
              </div>
            </div>
          </div>
        </Card>
      ))}
      {modal&&<Modal title="Nouvelle commande" onClose={()=>setModal(null)}
        footer={<><Btn sm outline onClick={()=>setModal(null)}>Annuler</Btn><Btn sm onClick={save}>Enregistrer</Btn></>}>
        <Input label="Service" value={form.service||""} onChange={f("service")} placeholder="Ex : Pack apéro"/>
        <Sel label="Logement" value={form.logement||""} onChange={f("logement")} options={lopts}/>
        <Input label="Nom voyageur" value={form.nomVoyageur||""} onChange={f("nomVoyageur")}/>
        <Input label="Date d'arrivée" value={form.dateArrivee||""} onChange={f("dateArrivee")} type="date"/>
        <Input label="Notes" value={form.notes||""} onChange={f("notes")} placeholder="Instructions spéciales..."/>
      </Modal>}
      {svcModal&&<Modal title="Nouveau service personnalisé" onClose={()=>setSvcModal(false)}
        footer={<><Btn sm outline onClick={()=>setSvcModal(false)}>Annuler</Btn><Btn sm onClick={addSvc} disabled={!newSvc.nom.trim()}>Ajouter</Btn></>}>
        <G2><Input label="Icône" value={newSvc.icon} onChange={v=>setNewSvc(s=>({...s,icon:v}))}/><Input label="Prix (€)" value={newSvc.prix} onChange={v=>setNewSvc(s=>({...s,prix:Number(v)}))} type="number"/></G2>
        <Input label="Nom du service *" value={newSvc.nom} onChange={v=>setNewSvc(s=>({...s,nom:v}))} required/>
        <Input label="Description" value={newSvc.desc} onChange={v=>setNewSvc(s=>({...s,desc:v}))} placeholder="Contenu du service..."/>
      </Modal>}
    </div>
  );
}

// ─── DOCS OBLIGATOIRES ────────────────────────────────────────────────────────
const DOCS_OBLIGATOIRES = [
  { id:"urssaf", label:"Déclaration URSSAF", icon:"🏛", desc:"Attestation de vigilance ou déclaration en cours", hasExpiry:false },
  { id:"rcpro",  label:"RC Professionnelle", icon:"🛡", desc:"Assurance responsabilité civile professionnelle", hasExpiry:true },
];

// helper: days until expiry
function daysUntil(dateStr) {
  if(!dateStr) return null;
  const diff = new Date(dateStr) - new Date();
  return Math.ceil(diff / 86400000);
}

// Check if a prestataire has missing/expired mandatory docs
function missingDocsCount(obligatoires) {
  if(!obligatoires) return DOCS_OBLIGATOIRES.length;
  return DOCS_OBLIGATOIRES.filter(d => {
    const entry = obligatoires[d.id];
    if(!entry || !entry.fourni) return true;
    if(d.hasExpiry && entry.expiry) {
      const days = daysUntil(entry.expiry);
      if(days !== null && days < 0) return true; // expired
    }
    return false;
  }).length;
}

function DocsObligatoiresSection({ prestataireId, nom, readOnly }) {
  const docRef = doc(db, "docs_obligatoires", prestataireId);
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(null);
  const fileRef = useRef(null);
  const [activeDoc, setActiveDoc] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(docRef, snap => {
      setData(snap.exists() ? snap.data() : {});
    });
    return unsub;
  }, [prestataireId]);

  const update = async (docId, field, value) => {
    setSaving(docId);
    const current = data || {};
    const entry = current[docId] || {};
    await setDoc(docRef, { ...current, [docId]: { ...entry, [field]: value, updatedAt: new Date().toISOString() } }, { merge: true });
    setSaving(null);
  };

  const handleFile = (docId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async ev => {
      await update(docId, "fichier", { name: file.name, type: file.type, url: ev.target.result });
      await update(docId, "fourni", true);
    };
    reader.readAsDataURL(file);
  };

  if (data === null) return <div style={{ color: T.gray2, fontSize: 12 }}>Chargement...</div>;

  const alertCount = missingDocsCount(data);

  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: T.navy }}>📋 Documents obligatoires</span>
        {alertCount > 0
          ? <span style={{ background: T.danger, color: "#fff", borderRadius: 20, padding: "2px 9px", fontSize: 11, fontWeight: 700 }}>⚠️ {alertCount} manquant{alertCount > 1 ? "s" : ""}</span>
          : <span style={{ background: "#D1FAE5", color: "#065F46", borderRadius: 20, padding: "2px 9px", fontSize: 11, fontWeight: 700 }}>✅ Complet</span>
        }
      </div>
      {DOCS_OBLIGATOIRES.map(d => {
        const entry = data[d.id] || {};
        const fourni = !!entry.fourni;
        const days = d.hasExpiry && entry.expiry ? daysUntil(entry.expiry) : null;
        const expired = days !== null && days < 0;
        const expireSoon = days !== null && days >= 0 && days <= 30;
        const statusColor = !fourni || expired ? T.danger : expireSoon ? T.warn : T.success;
        const statusBg = !fourni || expired ? "#FEE2E2" : expireSoon ? "#FEF3C7" : "#D1FAE5";
        const statusLabel = !fourni ? "Manquant" : expired ? "Expiré" : expireSoon ? `Expire dans ${days}j` : "✅ Valide";
        return (
          <div key={d.id} style={{ background: fourni && !expired ? "#F0FDF4" : "#FEF2F2", borderRadius: 10, border: `1px solid ${statusColor}22`, padding: "12px 14px", marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 3 }}>
                  <span style={{ fontSize: 17 }}>{d.icon}</span>
                  <span style={{ fontWeight: 700, fontSize: 14 }}>{d.label}</span>
                  <span style={{ background: statusBg, color: statusColor, borderRadius: 20, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>{statusLabel}</span>
                </div>
                <div style={{ color: T.gray, fontSize: 12 }}>{d.desc}</div>
                {entry.fichier && <div style={{ color: T.teal, fontSize: 11, marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>📎 {entry.fichier.name}</div>}
                {d.hasExpiry && fourni && (
                  <div style={{ marginTop: 6 }}>
                    {readOnly
                      ? entry.expiry && <div style={{ fontSize: 12, color: expired ? T.danger : T.gray }}>📅 Expiration : {entry.expiry}</div>
                      : <div>
                          <label style={{ fontSize: 11, fontWeight: 700, color: T.navy, display: "block", marginBottom: 3 }}>Date d'expiration</label>
                          <input type="date" value={entry.expiry || ""} onChange={e => update(d.id, "expiry", e.target.value)}
                            style={{ padding: "6px 10px", border: `1.5px solid ${expired ? T.danger : expireSoon ? T.warn : T.border}`, borderRadius: 8, fontSize: 12, outline: "none", background: T.white }} />
                        </div>
                    }
                  </div>
                )}
              </div>
              {!readOnly && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
                  <button onClick={() => { setActiveDoc(d.id); setTimeout(() => fileRef.current?.click(), 50); }}
                    style={{ padding: "6px 12px", borderRadius: 8, border: `1.5px solid ${T.teal}`, background: T.white, color: T.teal, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                    📎 {entry.fichier ? "Remplacer" : "Ajouter fichier"}
                  </button>
                  {!fourni && <button onClick={() => update(d.id, "fourni", true)}
                    style={{ padding: "6px 12px", borderRadius: 8, border: "none", background: T.success, color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                    ✅ Marquer fourni
                  </button>}
                  {fourni && <button onClick={() => update(d.id, "fourni", false)}
                    style={{ padding: "6px 12px", borderRadius: 8, border: "none", background: "#FEE2E2", color: "#991B1B", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                    ✕ Retirer
                  </button>}
                </div>
              )}
            </div>
            {saving === d.id && <div style={{ fontSize: 10, color: T.gray2, marginTop: 4 }}>Enregistrement...</div>}
          </div>
        );
      })}
      <input ref={fileRef} type="file" accept="image/*,.pdf" style={{ display: "none" }}
        onChange={e => { if (activeDoc) handleFile(activeDoc, e); }} />
    </div>
  );
}

// ─── DOCUMENTS & SIGNATURES ───────────────────────────────────────────────────
function Documents({ documents, prestataires, user, docsObligatoires }) {
  const [modal,setModal] = useState(null);
  const [step,setStep] = useState(1);
  const [sigG,setSigG] = useState(null);
  const [sigP,setSigP] = useState(null);
  const [form,setForm] = useState({});
  const [sigForm,setSigForm] = useState({ bpa:"",nom:"",lieu:"Masevaux",date:new Date().toISOString().slice(0,10),lu:false });
  const f = k=>v=>setForm(p=>({...p,[k]:v}));
  const sf = k=>v=>setSigForm(p=>({...p,[k]:v}));
  const NOW=new Date(),TIME=`${String(NOW.getHours()).padStart(2,"0")}:${String(NOW.getMinutes()).padStart(2,"0")}:${String(NOW.getSeconds()).padStart(2,"0")}`;
  const openSign=d=>{ setModal(d); setStep(1); setSigG(d.trace?.gestionnaire||null); setSigP(d.trace?.prestataire||null); setSigForm({bpa:"",nom:"",lieu:"Masevaux",date:new Date().toISOString().slice(0,10),lu:false}); };
  const saveDoc=async(docId,traceG,traceP)=>{
    const statut=traceG&&traceP?"signed":traceG||traceP?"partial":"pending";
    await updateDoc(doc(db,"documents",docId),{statut,trace:{gestionnaire:traceG,prestataire:traceP},updatedAt:serverTimestamp()});
    setModal(null);
  };
  const addDoc2=async()=>{
    if(!form.titre?.trim()) return;
    await addDoc(collection(db,"documents"),{...form,statut:"pending",trace:{gestionnaire:null,prestataire:null},createdAt:serverTimestamp()});
    setForm({});
  };
  const del=async id=>{ if(window.confirm("Supprimer ?")) await deleteDoc(doc(db,"documents",id)); };
  const popts=[{value:"",label:"— Choisir —"},...prestataires.map(p=>({value:`${p.prenom} ${p.nom}`,label:`${p.prenom} ${p.nom}`}))];
  const ST={ draft:{lbl:"Brouillon",bg:"#F3F4F6",c:T.gray},pending:{lbl:"À signer",bg:"#FEF3C7",c:"#92400E"},partial:{lbl:"Partielle",bg:"#DBEAFE",c:"#1E40AF"},signed:{lbl:"✅ Signé",bg:"#D1FAE5",c:"#065F46"} };
  const canVal=sigForm.bpa.toLowerCase().includes("bon pour accord")&&sigForm.nom.trim().length>3&&sigForm.lu&&true;
  const validSig=(isSigG)=>{
    const trace={...sigForm,heure:TIME,ip:"91.88."+Math.floor(Math.random()*200)+"."+Math.floor(Math.random()*200)};
    if(isSigG){ setSigG(trace); setStep(3); }
    else{ setSigP(trace); setStep(4); }
    setSigForm({bpa:"",nom:"",lieu:"Masevaux",date:new Date().toISOString().slice(0,10),lu:false});
  };
  const myDocs = user.role==="prestataire"?documents.filter(d=>d.prestataire===user.name||d.prestataire==="Tous"):documents;
  const findPrestByName = name => prestataires.find(p=>`${p.prenom} ${p.nom}`===name||p.name===name);
  return (
    <div>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14 }}>
        <SectionTitle>Documents</SectionTitle>
      </div>

      {/* Prestataire — docs obligatoires */}
      {user.role==="prestataire"&&<div style={{ background:"#FEF2F2",border:`1px solid ${T.danger}`,borderRadius:12,padding:"14px",marginBottom:14 }}>
        <div style={{ fontWeight:700,color:T.danger,fontSize:14,marginBottom:6 }}>📋 Documents obligatoires LOMMAX</div>
        <div style={{ fontSize:12,color:"#374151",marginBottom:10 }}>Ces documents sont requis avant toute prestation. Merci de les fournir au plus vite.</div>
        {findPrestByName(user.name)
          ? <DocsObligatoiresSection prestataireId={findPrestByName(user.name).id} nom={user.name} readOnly={false}/>
          : <div style={{ fontSize:12,color:T.gray }}>Votre fiche n'est pas encore créée par le gestionnaire.</div>}
      </div>}

      {/* Manager — alerte globale */}
      {user.role==="manager"&&docsObligatoires&&(()=>{
        const missing=prestataires.filter(p=>p.actif&&missingDocsCount(docsObligatoires[p.id])>0);
        if(!missing.length) return null;
        return <div style={{ background:"#FEF2F2",border:`1px solid ${T.danger}`,borderRadius:12,padding:"12px 14px",marginBottom:14 }}>
          <div style={{ fontWeight:700,color:T.danger,marginBottom:8,fontSize:14 }}>⚠️ Documents obligatoires manquants ({missing.length} prestataire{missing.length>1?"s":""})</div>
          {missing.map(p=>{
            const n=missingDocsCount(docsObligatoires[p.id]);
            return <div key={p.id} style={{ display:"flex",justifyContent:"space-between",padding:"5px 0",borderBottom:"1px solid #FECACA",fontSize:13 }}>
              <span style={{ fontWeight:600 }}>{p.prenom} {p.nom}</span>
              <span style={{ color:T.danger,fontWeight:700 }}>{n} doc{n>1?"s":""} manquant{n>1?"s":""}</span>
            </div>;
          })}
          <div style={{ marginTop:10 }}>
            {missing.map(p=><div key={p.id} style={{ marginBottom:14 }}>
              <div style={{ fontWeight:700,fontSize:13,color:T.navy,marginBottom:4 }}>👷 {p.prenom} {p.nom}</div>
              <DocsObligatoiresSection prestataireId={p.id} nom={`${p.prenom} ${p.nom}`} readOnly={false}/>
            </div>)}
          </div>
        </div>;
      })()}

      {user.role==="manager"&&<Card style={{ marginBottom:14 }}>
        <H3>Nouveau document</H3>
        <Input label="Titre *" value={form.titre||""} onChange={f("titre")} placeholder="Ex : Contrat prestataire — Nom"/>
        <Sel label="Type" value={form.type||"Contrat prestataire"} onChange={f("type")} options={["Contrat prestataire","Document interne"]}/>
        <Sel label="Prestataire concerné" value={form.prestataire||""} onChange={f("prestataire")} options={popts}/>
        <Input label="Date" value={form.date||""} onChange={f("date")} type="date"/>
        <Btn sm onClick={addDoc2} disabled={!form.titre?.trim()} full>Créer le document</Btn>
      </Card>}
      <div style={{ background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:10,padding:"9px 13px",fontSize:12,color:"#1E40AF",marginBottom:14,fontWeight:600 }}>
        ⚖️ Valeur légale : mention "Bon pour accord" + nom/prénom + date/lieu + signature + horodatage IP
      </div>
      {myDocs.length===0&&<Empty icon="📄" text="Aucun document."/>}
      {myDocs.map(d=>(
        <Card key={d.id} accent={d.statut==="signed"?T.success:d.statut==="partial"?T.teal:T.warn}>
          <div style={{ display:"flex",alignItems:"center",gap:12 }}>
            <div style={{ width:40,height:40,borderRadius:10,background:ST[d.statut]?.bg||"#F3F4F6",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0 }}>{d.statut==="signed"?"✅":"📋"}</div>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:700,marginBottom:3 }}>{d.titre}</div>
              <div style={{ fontSize:11,color:T.gray,display:"flex",gap:8,flexWrap:"wrap" }}>
                {d.date&&<span>📅 {d.date}</span>}
                {d.prestataire&&<span>👤 {d.prestataire}</span>}
              </div>
              <div style={{ marginTop:5,display:"flex",gap:5,flexWrap:"wrap" }}>
                <Bdg color={ST[d.statut]?.c} bg={ST[d.statut]?.bg}>{ST[d.statut]?.lbl}</Bdg>
                {d.trace?.gestionnaire&&<span style={{ fontSize:11,color:T.success,fontWeight:600 }}>👔 ✓</span>}
                {d.trace?.prestataire&&<span style={{ fontSize:11,color:T.success,fontWeight:600 }}>👷 ✓</span>}
              </div>
            </div>
            <div style={{ display:"flex",gap:5 }}>
              {d.statut!=="signed"&&<Btn sm onClick={()=>openSign(d)}>✍️</Btn>}
              {user.role==="manager"&&<Btn sm danger onClick={()=>del(d.id)}>🗑</Btn>}
            </div>
          </div>
        </Card>
      ))}
      {/* Modal signature */}
      {modal&&<div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,.55)",zIndex:1000,display:"flex",alignItems:"flex-end",justifyContent:"center" }} onClick={e=>e.target===e.currentTarget&&setModal(null)}>
        <div style={{ background:T.white,borderRadius:"18px 18px 0 0",width:"100%",maxWidth:560,maxHeight:"92vh",display:"flex",flexDirection:"column" }}>
          <div style={{ display:"flex",justifyContent:"center",padding:"10px 0 0" }}><div style={{ width:36,height:4,borderRadius:2,background:T.border }}/></div>
          <div style={{ padding:"12px 20px 10px",borderBottom:`1px solid ${T.border}` }}>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center" }}>
              <div style={{ fontFamily:"Raleway",fontWeight:800,fontSize:16 }}>{modal.titre}</div>
              <button onClick={()=>setModal(null)} style={{ background:T.bg,border:"none",width:30,height:30,borderRadius:"50%",cursor:"pointer",fontSize:18,display:"flex",alignItems:"center",justifyContent:"center",color:T.gray }}>×</button>
            </div>
            {/* Steps */}
            <div style={{ display:"flex",gap:0,marginTop:14 }}>
              {[{n:1,l:"Lecture"},{n:2,l:"Gestionnaire"},{n:3,l:"Prestataire"},{n:4,l:"Finalisé"}].map(s=>(
                <div key={s.n} style={{ flex:1,textAlign:"center",position:"relative" }}>
                  {s.n<4&&<div style={{ position:"absolute",top:14,left:"50%",width:"100%",height:2,background:T.border,zIndex:0 }}/>}
                  <div style={{ width:28,height:28,borderRadius:"50%",display:"inline-flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,border:`2px solid ${step>s.n?T.success:step===s.n?T.teal:T.border}`,background:step>s.n?T.success:step===s.n?T.teal:T.white,color:step>s.n||step===s.n?"#fff":T.gray2,position:"relative",zIndex:1 }}>{step>s.n?"✓":s.n}</div>
                  <div style={{ fontSize:10,color:T.gray,marginTop:3,fontWeight:600 }}>{s.l}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ flex:1,overflowY:"auto",padding:"16px 20px" }}>
            {step===1&&<div>
              <div style={{ background:"#FFFEF5",border:"1px solid #FDE68A",borderRadius:10,padding:16,fontSize:12,color:"#374151",lineHeight:1.8,maxHeight:220,overflowY:"auto",marginBottom:14 }}>
                <div style={{ fontWeight:800,textAlign:"center",marginBottom:12,fontSize:14 }}>CONTRAT DE PRESTATION DE SERVICES</div>
                <p><strong>Entre :</strong> LOMMAX / Société Louis 9 SAS, représentée par M. Frédéric Arbogast,</p>
                <p><strong>Et :</strong> {modal.prestataire}, ci-après « le Prestataire ».</p>
                <p><strong>Article 1 — Objet :</strong> Le Prestataire effectue des missions de conciergerie et d'entretien pour LOMMAX selon le barème PPL en vigueur.</p>
                <p><strong>Article 2 — Durée :</strong> Contrat à durée indéterminée, avec préavis de 15 jours.</p>
                <p><strong>Article 3 — Obligations :</strong> Respect des consignes, confidentialité, signalement incidents sous 24h.</p>
                <p><strong>Fait à Masevaux, le {modal.date}</strong></p>
              </div>
              <Btn full onClick={()=>setStep(2)}>J'ai lu le document → Procéder aux signatures</Btn>
            </div>}
            {(step===2||step===3)&&<div>
              <div style={{ border:`2px solid ${T.border}`,borderRadius:12,overflow:"hidden",marginBottom:12 }}>
                <div style={{ background:T.navy,color:"#fff",padding:"10px 14px",fontSize:12,fontWeight:700 }}>{step===2?"👔 Signature Gestionnaire — Frédéric Arbogast":`👷 Signature Prestataire — ${modal.prestataire}`}</div>
                <div style={{ padding:14 }}>
                  {(step===2?sigG:sigP)?<div style={{ background:"#D1FAE5",border:"1px solid #6EE7B7",borderRadius:9,padding:"10px 12px",display:"flex",alignItems:"center",gap:8,fontSize:12,color:"#065F46",fontWeight:600 }}>
                    ✅ Signature validée — {(step===2?sigG:sigP)?.nom} · {(step===2?sigG:sigP)?.date}
                    <button onClick={()=>step===2?setSigG(null):setSigP(null)} style={{ marginLeft:"auto",padding:"3px 8px",borderRadius:7,border:`1px solid ${T.border}`,background:T.white,cursor:"pointer",fontSize:11,color:T.gray }}>Modifier</button>
                  </div>:<>
                    <div style={{ marginBottom:10 }}>
                      <label style={{ display:"flex",alignItems:"center",gap:8,background:"#F0FDF4",border:"1.5px solid #86EFAC",borderRadius:9,padding:"10px 12px",cursor:"pointer",marginBottom:10 }}>
                        <input type="checkbox" checked={sigForm.lu} onChange={e=>sf("lu")(e.target.checked)} style={{ width:17,height:17,accentColor:T.success }}/>
                        <span style={{ fontSize:13,fontWeight:600,color:"#065F46" }}>J'ai lu et j'approuve l'intégralité du document</span>
                      </label>
                      <label style={{ fontSize:12,fontWeight:700,color:T.navy,marginBottom:5,display:"block" }}>Mention obligatoire — saisir "Bon pour accord"</label>
                      <input value={sigForm.bpa} onChange={e=>sf("bpa")(e.target.value)} placeholder='Saisir : "Bon pour accord"'
                        style={{ width:"100%",padding:"9px 12px",border:`2px solid ${sigForm.bpa.toLowerCase().includes("bon pour accord")?"#0EA572":T.border}`,borderRadius:9,fontSize:13,background:sigForm.bpa.toLowerCase().includes("bon pour accord")?"#F0FDF4":"#FFFEF5",fontFamily:"Georgia,serif",outline:"none" }}/>
                      {sigForm.bpa.length>0&&!sigForm.bpa.toLowerCase().includes("bon pour accord")&&<div style={{ color:T.danger,fontSize:11,marginTop:2 }}>⚠️ Saisir exactement "Bon pour accord"</div>}
                    </div>
                    <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10 }}>
                      <div><label style={{ fontSize:12,fontWeight:700,color:T.navy,display:"block",marginBottom:4 }}>Nom et prénom *</label><input value={sigForm.nom} onChange={e=>sf("nom")(e.target.value)} style={{ width:"100%",padding:"8px 11px",border:`1.5px solid ${T.border}`,borderRadius:9,fontSize:13,outline:"none" }}/></div>
                      <div><label style={{ fontSize:12,fontWeight:700,color:T.navy,display:"block",marginBottom:4 }}>Lieu</label><input value={sigForm.lieu} onChange={e=>sf("lieu")(e.target.value)} style={{ width:"100%",padding:"8px 11px",border:`1.5px solid ${T.border}`,borderRadius:9,fontSize:13,outline:"none" }}/></div>
                    </div>
                    <div style={{ marginBottom:10 }}>
                      <label style={{ fontSize:12,fontWeight:700,color:T.navy,display:"block",marginBottom:5 }}>Signature manuscrite *</label>
                      <SigPad onSave={()=>{}} onClear={()=>{}}/>
                    </div>
                    <div style={{ background:T.bg,borderRadius:9,padding:"10px 12px",fontSize:11,color:T.gray,marginBottom:12 }}>
                      <div style={{ fontWeight:700,color:T.navy,marginBottom:4 }}>Validité légale :</div>
                      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:3 }}>
                        {[{v:sigForm.lu,l:"Lu et approuvé"},{v:sigForm.bpa.toLowerCase().includes("bon pour accord"),l:"Bon pour accord"},{v:sigForm.nom.trim().length>3,l:"Nom et prénom"},{v:true,l:"Signature dessinée"}].map(x=><span key={x.l} style={{ color:x.v?T.success:T.danger }}>{x.v?"✅":"❌"} {x.l}</span>)}
                      </div>
                    </div>
                    <Btn full onClick={()=>validSig(step===2)} disabled={!canVal}>✅ Valider ma signature</Btn>
                  </>}
                </div>
              </div>
              <div style={{ display:"flex",gap:8,justifyContent:"space-between",marginTop:8 }}>
                <Btn sm outline onClick={()=>setStep(step-1)}>← Retour</Btn>
                {(step===2?sigG:sigP)&&<Btn sm onClick={()=>step===2?setStep(3):setStep(4)}>Continuer →</Btn>}
              </div>
            </div>}
            {step===4&&<div style={{ textAlign:"center",padding:"10px 0" }}>
              <div style={{ fontSize:48,marginBottom:10 }}>🎉</div>
              <div style={{ fontWeight:800,fontSize:17,color:T.success,marginBottom:6 }}>Contrat signé !</div>
              <div style={{ color:T.gray,fontSize:13,marginBottom:16 }}>Les deux parties ont signé.<br/>Horodatage enregistré.</div>
              <div style={{ background:"#F8FAFC",borderRadius:10,padding:"10px 14px",fontSize:11,color:T.gray,marginBottom:16,textAlign:"left" }}>
                🔒 Gestionnaire : {sigG?.nom} · {sigG?.date} {sigG?.heure} · IP {sigG?.ip}<br/>
                🔒 Prestataire : {sigP?.nom} · {sigP?.date} {sigP?.heure} · IP {sigP?.ip}
              </div>
              <div style={{ display:"flex",gap:8,justifyContent:"center" }}>
                <Btn onClick={()=>alert("PDF téléchargeable en production")} outline>📥 PDF</Btn>
                <Btn onClick={()=>saveDoc(modal.id,sigG,sigP)}>💾 Enregistrer</Btn>
              </div>
            </div>}
          </div>
        </div>
      </div>}
    </div>
  );
}

// ─── ANNONCES ─────────────────────────────────────────────────────────────────
function Annonces({ annonces, prestataires, user }) {
  const [text,setText] = useState("");
  const [dest,setDest] = useState("tous");
  const [fileInfo,setFileInfo] = useState(null);
  const fileRef = useRef(null);
  const bottomRef = useRef(null);
  const myMessages = user.role==="manager"?annonces:annonces.filter(a=>a.dest==="tous"||a.dest===user.name||a.from===user.name);
  useEffect(()=>{ bottomRef.current?.scrollIntoView({behavior:"smooth"}); },[myMessages]);
  const send = async()=>{
    if(!text.trim()&&!fileInfo) return;
    const msg={ from:user.name,fromRole:user.role,dest:user.role==="manager"?dest:"gestionnaire",text:text.trim(),file:fileInfo||null,createdAt:serverTimestamp() };
    await addDoc(collection(db,"annonces"),msg);
    setText(""); setFileInfo(null);
  };
  const handleFile = e=>{
    const file=e.target.files[0]; if(!file) return;
    const reader=new FileReader();
    reader.onload=ev=>setFileInfo({name:file.name,type:file.type,size:file.size,url:ev.target.result});
    reader.readAsDataURL(file);
  };
  const popts=[{value:"tous",label:"📢 Tous les prestataires"},...prestataires.map(p=>({value:p.name||`${p.prenom} ${p.nom}`,label:`👤 ${p.prenom} ${p.nom}`}))];
  const isImg=type=>type?.startsWith("image/");
  const isPDF=type=>type==="application/pdf";
  return (
    <div style={{ display:"flex",flexDirection:"column",height:"calc(100vh - 120px)",maxHeight:600 }}>
      <SectionTitle>Annonces</SectionTitle>
      <div style={{ flex:1,overflowY:"auto",paddingBottom:8 }}>
        {myMessages.length===0&&<Empty icon="💬" text="Aucun message. Soyez le premier à écrire !"/>}
        {myMessages.map(m=>{
          const isMe=m.from===user.name;
          return <div key={m.id} style={{ display:"flex",justifyContent:isMe?"flex-end":"flex-start",marginBottom:10 }}>
            <div style={{ maxWidth:"80%",background:isMe?T.teal:T.white,color:isMe?"#fff":T.navy,borderRadius:isMe?"14px 14px 4px 14px":"14px 14px 14px 4px",padding:"10px 13px",border:isMe?"none":`1px solid ${T.border}`,boxShadow:"0 1px 3px rgba(0,0,0,.07)" }}>
              {!isMe&&<div style={{ fontSize:11,fontWeight:700,color:T.teal,marginBottom:3 }}>{m.from}{m.dest&&m.dest!=="tous"&&m.dest!=="gestionnaire"&&<span style={{ color:T.gray2 }}> → {m.dest}</span>}</div>}
              {m.dest==="tous"&&isMe&&<div style={{ fontSize:10,color:"rgba(255,255,255,.7)",marginBottom:3 }}>→ Tous les prestataires</div>}
              {m.text&&<div style={{ fontSize:13,lineHeight:1.5,wordBreak:"break-word" }}>{m.text}</div>}
              {m.file&&isImg(m.file.type)&&<img src={m.file.url} alt={m.file.name} style={{ maxWidth:220,borderRadius:8,marginTop:m.text?6:0,display:"block" }}/>}
              {m.file&&isPDF(m.file.type)&&<div style={{ marginTop:m.text?6:0,background:isMe?"rgba(255,255,255,.2)":"#F3F4F6",borderRadius:8,padding:"6px 10px",fontSize:12,display:"flex",alignItems:"center",gap:6 }}>📄 {m.file.name}</div>}
              {m.file&&!isImg(m.file.type)&&!isPDF(m.file.type)&&<div style={{ marginTop:m.text?6:0,fontSize:12,display:"flex",alignItems:"center",gap:5,opacity:.8 }}>🔗 {m.file.name}</div>}
              <div style={{ fontSize:10,opacity:.6,marginTop:4,textAlign:isMe?"right":"left" }}>{m.createdAt?.toDate?.()?.toLocaleTimeString("fr-FR",{hour:"2-digit",minute:"2-digit"})||""}</div>
            </div>
          </div>;
        })}
        <div ref={bottomRef}/>
      </div>
      {/* Barre d'envoi */}
      <div style={{ background:T.white,borderRadius:14,border:`1px solid ${T.border}`,padding:"10px 12px",boxShadow:"0 -2px 8px rgba(0,0,0,.05)" }}>
        {user.role==="manager"&&<div style={{ marginBottom:8 }}>
          <select value={dest} onChange={e=>setDest(e.target.value)}
            style={{ width:"100%",padding:"7px 10px",border:`1px solid ${T.border}`,borderRadius:9,fontSize:12,background:T.bg,color:T.navy,outline:"none" }}>
            {popts.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>}
        {fileInfo&&<div style={{ background:T.bg,borderRadius:8,padding:"5px 10px",fontSize:11,marginBottom:8,display:"flex",alignItems:"center",gap:6 }}>
          {isImg(fileInfo.type)?<img src={fileInfo.url} style={{ width:32,height:32,objectFit:"cover",borderRadius:4 }}/>:<span>📄</span>}
          <span style={{ flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{fileInfo.name}</span>
          <button onClick={()=>setFileInfo(null)} style={{ background:"none",border:"none",cursor:"pointer",color:T.danger,fontSize:14,fontWeight:700 }}>×</button>
        </div>}
        <div style={{ display:"flex",gap:8,alignItems:"flex-end" }}>
          <textarea value={text} onChange={e=>setText(e.target.value)} placeholder="Écrivez un message..." rows={2}
            style={{ flex:1,padding:"8px 10px",border:`1px solid ${T.border}`,borderRadius:10,fontSize:13,resize:"none",outline:"none",fontFamily:"inherit" }}
            onKeyDown={e=>{ if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();} }}/>
          <div style={{ display:"flex",flexDirection:"column",gap:5 }}>
            <button onClick={()=>fileRef.current?.click()} style={{ width:36,height:36,borderRadius:10,border:`1px solid ${T.border}`,background:T.bg,cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center" }}>📎</button>
            <button onClick={send} disabled={!text.trim()&&!fileInfo}
              style={{ width:36,height:36,borderRadius:10,border:"none",background:text.trim()||fileInfo?T.teal:"#D1D5DB",cursor:text.trim()||fileInfo?"pointer":"default",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff" }}>↑</button>
          </div>
        </div>
        <input ref={fileRef} type="file" accept="image/*,.pdf" style={{ display:"none" }} onChange={handleFile}/>
      </div>
    </div>
  );
}

// ─── PLANNING PRESTATAIRE ─────────────────────────────────────────────────────
function Planning({ ppls, user }) {
  const now=new Date();
  const [year,setYear] = useState(now.getFullYear());
  const [month,setMonth] = useState(now.getMonth());
  const [selDay,setSelDay] = useState(null);
  const [view,setView] = useState("cal");
  const mesAssignes=ppls.filter(p=>p.statut==="assigné"&&p.prestataire===user.name);
  const dispos=ppls.filter(p=>p.statut==="disponible");
  const postuler=async ppl=>{ if(ppl.candidats?.includes(user.name)) return; await updateDoc(doc(db,"ppls",ppl.id),{candidats:[...(ppl.candidats||[]),user.name],updatedAt:serverTimestamp()}); };
  const prev=()=>{ if(month===0){setMonth(11);setYear(y=>y-1);}else setMonth(m=>m-1); setSelDay(null); };
  const next=()=>{ if(month===11){setMonth(0);setYear(y=>y+1);}else setMonth(m=>m+1); setSelDay(null); };
  const days=(()=>{
    const first=new Date(year,month,1),last=new Date(year,month+1,0),startDow=(first.getDay()+6)%7,cells=[];
    for(let i=0;i<startDow;i++){const d=new Date(year,month,1-startDow+i);cells.push({date:d,cur:false});}
    for(let d=1;d<=last.getDate();d++) cells.push({date:new Date(year,month,d),cur:true});
    while(cells.length%7!==0){const l=cells[cells.length-1].date,nd=new Date(l);nd.setDate(nd.getDate()+1);cells.push({date:nd,cur:false});}
    return cells;
  })();
  const toStr=d=>`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  const todayStr=toStr(now);
  const allPPL=[...mesAssignes,...dispos];
  const evOnDay=d=>{ const s=toStr(d); return allPPL.filter(p=>p.date===s); };
  const selEvs=selDay?evOnDay(selDay):[];
  const gcalLink=p=>{
    const d=p.date.replace(/-/g,""),[h,m]=(p.heure||"09:00").split(":").map(Number);
    const start=`${d}T${String(h).padStart(2,"0")}${String(m).padStart(2,"0")}00`;
    const dur=p.duree?.includes("h")?parseInt(p.duree)*60:60;
    const endH=h+Math.floor(dur/60),endM=m+(dur%60);
    const end=`${d}T${String(endH).padStart(2,"0")}${String(endM).padStart(2,"0")}00`;
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent("[LOMMAX] "+p.titre)}&dates=${start}/${end}&location=${encodeURIComponent(p.logement||"")}&details=${encodeURIComponent("Durée : "+(p.duree||""))}`;
  };
  const downloadICS=()=>{
    const lines=["BEGIN:VCALENDAR","VERSION:2.0","PRODID:-//LOMMAX//FR","CALSCALE:GREGORIAN"];
    mesAssignes.forEach(p=>{ const d=p.date.replace(/-/g,""),[h,m]=(p.heure||"09:00").split(":").map(Number),start=`${d}T${String(h).padStart(2,"0")}${String(m).padStart(2,"0")}00`,dur=p.duree?.includes("h")?parseInt(p.duree)*60:60,endH=h+Math.floor(dur/60),endM=m+(dur%60),end=`${d}T${String(endH).padStart(2,"0")}${String(endM).padStart(2,"0")}00`;lines.push("BEGIN:VEVENT",`DTSTART:${start}`,`DTEND:${end}`,`SUMMARY:[LOMMAX] ${p.titre}`,`LOCATION:${p.logement||""}`,`DESCRIPTION:${p.duree||""}\\n${p.notes||""}`,`STATUS:CONFIRMED`,"END:VEVENT"); });
    lines.push("END:VCALENDAR");
    const blob=new Blob([lines.join("\r\n")],{type:"text/calendar"});
    const url=URL.createObjectURL(blob),a=document.createElement("a");a.href=url;a.download=`lommax-planning-${user.name}.ics`;document.body.appendChild(a);a.click();document.body.removeChild(a);URL.revokeObjectURL(url);
  };
  return (
    <div>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14 }}>
        <SectionTitle>Mon Planning</SectionTitle>
        <div style={{ display:"flex",gap:5 }}>
          {[["cal","📅"],["list","📋"]].map(([id,ic])=>(
            <button key={id} onClick={()=>setView(id)} style={{ padding:"6px 12px",borderRadius:9,border:`1.5px solid ${view===id?T.navy:T.border}`,background:view===id?T.navy:T.white,color:view===id?"#fff":T.gray,fontSize:12,fontWeight:700,cursor:"pointer" }}>{ic}</button>
          ))}
        </div>
      </div>
      <div style={{ background:T.white,borderRadius:12,border:`1px solid ${T.border}`,padding:"12px 14px",marginBottom:14,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10 }}>
        <div style={{ fontSize:13,color:T.gray }}><strong style={{ color:T.navy }}>{mesAssignes.length}</strong> prestation{mesAssignes.length>1?"s":""} assignée{mesAssignes.length>1?"s":""}</div>
        <div style={{ display:"flex",gap:8 }}>
          <button onClick={downloadICS} style={{ display:"inline-flex",alignItems:"center",gap:5,padding:"6px 12px",borderRadius:8,border:`1.5px solid ${T.success}`,background:T.white,color:T.success,fontSize:12,fontWeight:700,cursor:"pointer" }}>📥 Export .ics</button>
          <a href="https://calendar.google.com/calendar/r" target="_blank" rel="noreferrer" style={{ display:"inline-flex",alignItems:"center",gap:5,padding:"6px 12px",borderRadius:8,border:"1.5px solid #1a73e8",background:T.white,color:"#1a73e8",fontSize:12,fontWeight:700,textDecoration:"none" }}>📆 Google Cal</a>
        </div>
      </div>
      <div style={{ fontSize:11,color:T.gray2,marginBottom:12 }}>💡 Cliquez "Export .ics" puis importez dans Google Calendar pour synchroniser vos prestations.</div>
      {view==="cal"&&<div>
        <div style={{ display:"flex",gap:8,marginBottom:10 }}>
          {[{bg:"#D1FAE5",c:"#065F46",l:"Mes prestations assignées"},{bg:"#DBEAFE",c:"#1E40AF",l:"Disponibles"}].map(x=>(
            <div key={x.l} style={{ display:"flex",alignItems:"center",gap:4,fontSize:11,color:T.gray }}><div style={{ width:8,height:8,borderRadius:2,background:x.bg,border:`1px solid ${x.c}` }}/>{x.l}</div>
          ))}
        </div>
        <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10 }}>
          <button onClick={prev} style={{ width:32,height:32,borderRadius:"50%",border:`1px solid ${T.border}`,background:T.white,cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center" }}>‹</button>
          <div style={{ fontFamily:"Raleway",fontWeight:800,fontSize:16,color:T.navy }}>{MONTHS[month]} {year}</div>
          <button onClick={next} style={{ width:32,height:32,borderRadius:"50%",border:`1px solid ${T.border}`,background:T.white,cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center" }}>›</button>
        </div>
        <div style={{ display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2 }}>
          {DAYS_SHORT.map(d=><div key={d} style={{ textAlign:"center",fontSize:10,fontWeight:700,color:T.gray2,padding:"4px 0" }}>{d}</div>)}
          {days.map((cell,i)=>{
            const evs=evOnDay(cell.date),isSel=selDay&&selDay.toDateString()===cell.date.toDateString(),isToday=toStr(cell.date)===todayStr;
            return <div key={i} onClick={()=>setSelDay(cell.date)}
              style={{ minHeight:58,borderRadius:8,padding:"3px 2px",border:`1.5px solid ${isSel?T.teal:isToday?T.teal:T.border}`,background:isSel?"#E0F7FF":isToday?"#F0F9FF":cell.cur?T.white:"#F8FAFC",opacity:cell.cur?1:.4,cursor:"pointer" }}>
              <div style={{ fontSize:11,fontWeight:700,color:isToday?T.teal:T.gray,paddingLeft:2 }}>{cell.date.getDate()}</div>
              {evs.slice(0,2).map(e=><div key={e.id} style={{ background:e.statut==="assigné"?"#D1FAE5":"#DBEAFE",color:e.statut==="assigné"?"#065F46":"#1E40AF",borderRadius:3,padding:"1px 3px",fontSize:9,fontWeight:700,marginBottom:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>
                {e.heure?" "+e.heure:""} {e.logement?.split(" ")[0]||e.titre}
              </div>)}
              {evs.length>2&&<div style={{ fontSize:9,color:T.gray2,paddingLeft:2 }}>+{evs.length-2}</div>}
            </div>;
          })}
        </div>
        {selDay&&selEvs.length>0&&<div style={{ background:T.white,borderRadius:12,border:`1px solid ${T.border}`,padding:14,marginTop:10 }}>
          <H3>{selDay.toLocaleDateString("fr-FR",{weekday:"long",day:"numeric",month:"long"})}</H3>
          {selEvs.map(e=><div key={e.id} style={{ background:e.statut==="assigné"?"#F0FDF4":"#EFF6FF",border:`1px solid ${e.statut==="assigné"?"#BBF7D0":"#BFDBFE"}`,borderRadius:9,padding:"10px 12px",marginBottom:8 }}>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:8 }}>
              <div>
                <div style={{ fontWeight:700,fontSize:14,marginBottom:3 }}>{e.titre}</div>
                <div style={{ color:T.gray,fontSize:12,display:"flex",gap:8,flexWrap:"wrap" }}>
                  {e.logement&&<span>📍 {e.logement}</span>}
                  {e.heure&&<span>🕐 {e.heure}</span>}
                  {e.duree&&<span>⏱ {e.duree}</span>}
                  {e.nbVoyageurs>0&&<span style={{ fontWeight:700,color:T.purple }}>👥 {e.nbVoyageurs} voy.</span>}
                </div>
                {e.notes&&<div style={{ color:T.gray2,fontSize:11,marginTop:3,fontStyle:"italic" }}>{e.notes}</div>}
              </div>
              <div style={{ display:"flex",gap:6 }}>
                {e.statut==="disponible"&&<button onClick={()=>postuler(e)} disabled={e.candidats?.includes(user.name)} style={{ padding:"5px 10px",borderRadius:8,border:"none",fontSize:11,fontWeight:700,background:e.candidats?.includes(user.name)?"#D1FAE5":T.success,color:e.candidats?.includes(user.name)?"#065F46":"#fff",cursor:e.candidats?.includes(user.name)?"default":"pointer" }}>{e.candidats?.includes(user.name)?"✅ Envoyé":"🙋 Postuler"}</button>}
                <a href={gcalLink(e)} target="_blank" rel="noreferrer" style={{ padding:"5px 10px",borderRadius:8,border:"1.5px solid #1a73e8",background:T.white,color:"#1a73e8",fontSize:11,fontWeight:700,textDecoration:"none",display:"inline-flex",alignItems:"center" }}>📆 +Cal</a>
              </div>
            </div>
          </div>)}
        </div>}
      </div>}
      {view==="list"&&<div>
        <H3 color={T.success}>✓ Mes prestations assignées ({mesAssignes.length})</H3>
        {mesAssignes.length===0&&<Empty icon="📋" text="Aucune prestation assignée."/>}
        {mesAssignes.map(p=><Card key={p.id} accent={T.success}>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8 }}>
            <div>
              <div style={{ fontWeight:700,marginBottom:3 }}>{p.titre}</div>
              <div style={{ color:T.gray,fontSize:12,display:"flex",gap:8,flexWrap:"wrap" }}>
                {p.logement&&<span>📍 {p.logement}</span>}
                {p.date&&<span>📅 {p.date}</span>}
                {p.heure&&<span>🕐 {p.heure}</span>}
                {p.duree&&<span>⏱ {p.duree}</span>}
                {p.nbVoyageurs>0&&<span style={{ fontWeight:700,color:T.purple }}>👥 {p.nbVoyageurs} voy.</span>}
              </div>
              {p.notes&&<div style={{ color:T.gray2,fontSize:11,marginTop:3,fontStyle:"italic" }}>{p.notes}</div>}
            </div>
            <a href={gcalLink(p)} target="_blank" rel="noreferrer" style={{ padding:"5px 10px",borderRadius:8,border:"1.5px solid #1a73e8",background:T.white,color:"#1a73e8",fontSize:11,fontWeight:700,textDecoration:"none",display:"inline-flex",alignItems:"center",flexShrink:0 }}>📆</a>
          </div>
        </Card>)}
        <H3 color={T.teal} style={{ marginTop:16 }}>○ Disponibles ({dispos.length})</H3>
        {dispos.map(p=><Card key={p.id} accent={T.teal}>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8 }}>
            <div>
              <div style={{ fontWeight:700,marginBottom:3 }}>{p.titre}</div>
              <div style={{ color:T.gray,fontSize:12,display:"flex",gap:8,flexWrap:"wrap" }}>
                {p.logement&&<span>📍 {p.logement}</span>}
                {p.date&&<span>📅 {p.date}</span>}
                {p.nbVoyageurs>0&&<span style={{ fontWeight:700,color:T.purple }}>👥 {p.nbVoyageurs} voy.</span>}
              </div>
            </div>
            <button onClick={()=>postuler(p)} disabled={p.candidats?.includes(user.name)}
              style={{ padding:"5px 10px",borderRadius:8,border:"none",fontSize:11,fontWeight:700,background:p.candidats?.includes(user.name)?"#D1FAE5":T.success,color:p.candidats?.includes(user.name)?"#065F46":"#fff",cursor:p.candidats?.includes(user.name)?"default":"pointer",flexShrink:0 }}>
              {p.candidats?.includes(user.name)?"✅ Envoyé":"🙋 Postuler"}
            </button>
          </div>
        </Card>)}
      </div>}
    </div>
  );
}

// ─── PORTAIL GESTIONNAIRE ─────────────────────────────────────────────────────
function ManagerPortal({ user, onLogout }) {
  const ppls = useCol("ppls");
  const logements = useCol("logements");
  const prestataires = useCol("prestataires");
  const stocks = useCol("stocks");
  const kits = useCol("kits");
  const taches = useCol("taches");
  const commandes = useCol("commandes");
  const evenements = useCol("evenements");
  const documents = useCol("documents");
  const annonces = useCol("annonces");
  const services = useCol("services");
  // docs obligatoires — map prestataireId => data
  const [docsObligatoires,setDocsObligatoires] = useState({});
  useEffect(()=>{
    if(!prestataires.length) return;
    const unsubs=prestataires.map(p=>{
      return onSnapshot(doc(db,"docs_obligatoires",p.id), snap=>{
        setDocsObligatoires(prev=>({...prev,[p.id]:snap.exists()?snap.data():{}}));
      });
    });
    return ()=>unsubs.forEach(u=>u());
  },[prestataires.map(p=>p.id).join(",")]);
  const [tab,setTab] = useState("dashboard");
  const candidats=ppls.filter(p=>p.candidats?.length>0&&p.statut==="disponible").length;
  const alertes=stocks.filter(s=>s.quantite<=s.minimum).length;
  const pendingServices=commandes.filter(c=>c.statut==="en_attente").length;
  const unread=annonces.filter(a=>a.fromRole==="prestataire").length;
  const missingDocs=prestataires.filter(p=>p.actif&&missingDocsCount(docsObligatoires[p.id])>0).length;
  const tabs=[
    { id:"dashboard",icon:"📊",lbl:"Dashboard" },
    { id:"logements",icon:"🏡",lbl:"Logements" },
    { id:"prestataires",icon:"👷",lbl:"Prestataires" },
    { id:"stocks",icon:"📦",lbl:"Stocks" },
    { id:"ppls",icon:"📋",lbl:"PPL",notif:candidats },
    { id:"taches",icon:"✅",lbl:"Tâches" },
    { id:"calendrier",icon:"📅",lbl:"Calendrier" },
    { id:"services",icon:"🛎",lbl:"Services",notif:pendingServices },
    { id:"documents",icon:"📄",lbl:"Documents",notif:missingDocs },
    { id:"annonces",icon:"💬",lbl:"Annonces",notif:unread },
  ];
  return (
    <div style={{ minHeight:"100vh",background:T.bg }}>
      <style>{css}</style>
      <div style={{ background:T.navy,height:52,display:"flex",alignItems:"center",justifyContent:"space-between",padding:"0 16px",position:"sticky",top:0,zIndex:200 }}>
        <div style={{ display:"flex",alignItems:"center",gap:10 }}>
          <div style={{ width:32,height:32,background:"rgba(75,163,195,.3)",borderRadius:9,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16 }}>🏡</div>
          <span style={{ fontFamily:"Raleway",fontWeight:800,fontSize:18,color:"#fff",letterSpacing:1.5 }}>LOMMAX</span>
          <span style={{ background:"rgba(255,255,255,.15)",color:"rgba(255,255,255,.9)",padding:"2px 9px",borderRadius:20,fontSize:11,fontWeight:600 }}>Gestionnaire</span>
        </div>
        <button onClick={onLogout} style={{ background:"rgba(255,255,255,.12)",border:"1px solid rgba(255,255,255,.2)",color:"#fff",padding:"5px 12px",borderRadius:8,cursor:"pointer",fontSize:12,fontWeight:600 }}>Déconnexion</button>
      </div>
      <div style={{ background:T.white,borderBottom:`1px solid ${T.border}`,display:"flex",overflowX:"auto",padding:"0 8px",position:"sticky",top:52,zIndex:199 }}>
        {tabs.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{ padding:"12px 10px",border:"none",background:"none",cursor:"pointer",fontSize:12,fontWeight:600,whiteSpace:"nowrap",color:tab===t.id?T.teal:T.gray2,borderBottom:tab===t.id?`2.5px solid ${T.teal}`:"2.5px solid transparent",display:"flex",alignItems:"center",gap:4,transition:"all .15s" }}>
            {t.icon} {t.lbl}
            {t.notif>0&&<span style={{ background:t.id==="ppls"?T.warn:T.danger,color:"#fff",borderRadius:10,padding:"1px 6px",fontSize:10,fontWeight:700 }}>{t.notif}</span>}
          </button>
        ))}
      </div>
      <div style={{ padding:16,maxWidth:900,margin:"0 auto" }}>
        {tab==="dashboard"&&<Dashboard ppls={ppls} logements={logements} prestataires={prestataires} stocks={stocks} taches={taches} commandes={commandes} kits={kits} docsObligatoires={docsObligatoires}/>}
        {tab==="logements"&&<Logements logements={logements}/>}
        {tab==="prestataires"&&<Prestataires prestataires={prestataires}/>}
        {tab==="stocks"&&<Stocks stocks={stocks} kits={kits} logements={logements}/>}
        {tab==="ppls"&&<PPL ppls={ppls} logements={logements} prestataires={prestataires} user={user}/>}
        {tab==="taches"&&<Taches taches={taches} logements={logements} prestataires={prestataires} user={user}/>}
        {tab==="calendrier"&&<Calendrier evenements={evenements} ppls={ppls} logements={logements}/>}
        {tab==="services"&&<Services commandes={commandes} logements={logements} services={services}/>}
        {tab==="documents"&&<Documents documents={documents} prestataires={prestataires} user={user} docsObligatoires={docsObligatoires}/>}
        {tab==="annonces"&&<Annonces annonces={annonces} prestataires={prestataires} user={user}/>}
      </div>
    </div>
  );
}

// ─── PORTAIL PRESTATAIRE ──────────────────────────────────────────────────────
function PrestatairePortal({ user, onLogout }) {
  const ppls = useCol("ppls");
  const taches = useCol("taches");
  const documents = useCol("documents");
  const annonces = useCol("annonces");
  const prestataires = useCol("prestataires");
  const [tab,setTab] = useState("ppls");
  const mesTaches=taches.filter(t=>t.assignea===user.name&&t.statut!=="terminé").length;
  const unread=annonces.filter(a=>a.fromRole!=="prestataire"&&(a.dest==="tous"||a.dest===user.name)).length;
  const docsToSign=documents.filter(d=>(d.prestataire===user.name||d.prestataire==="Tous")&&d.statut!=="signed").length;
  const myPrest=prestataires.find(p=>p.name===user.name||`${p.prenom} ${p.nom}`===user.name);
  const [myObligatoires,setMyObligatoires] = useState(null);
  useEffect(()=>{
    if(!myPrest) return;
    return onSnapshot(doc(db,"docs_obligatoires",myPrest.id),snap=>setMyObligatoires(snap.exists()?snap.data():{}));
  },[myPrest?.id]);
  const missingMesDocs = myObligatoires!==null ? missingDocsCount(myObligatoires) : DOCS_OBLIGATOIRES.length;
  const docsBadge = docsToSign + missingMesDocs;
  const tabs=[
    { id:"ppls",icon:"📋",lbl:"Prestations" },
    { id:"planning",icon:"📅",lbl:"Planning" },
    { id:"taches",icon:"✅",lbl:"Tâches",notif:mesTaches },
    { id:"documents",icon:"📄",lbl:"Documents",notif:docsBadge },
    { id:"annonces",icon:"💬",lbl:"Annonces",notif:unread },
  ];
  return (
    <div style={{ minHeight:"100vh",background:T.bg }}>
      <style>{css}</style>
      <div style={{ background:T.navy,height:52,display:"flex",alignItems:"center",justifyContent:"space-between",padding:"0 16px",position:"sticky",top:0,zIndex:200 }}>
        <div style={{ display:"flex",alignItems:"center",gap:10 }}>
          <div style={{ width:32,height:32,background:"rgba(75,163,195,.3)",borderRadius:9,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16 }}>🏡</div>
          <span style={{ fontFamily:"Raleway",fontWeight:800,fontSize:18,color:"#fff" }}>LOMMAX</span>
          <span style={{ background:"rgba(75,163,195,.3)",color:"#E8F4F8",padding:"2px 9px",borderRadius:20,fontSize:11,fontWeight:600 }}>👤 {user.name}</span>
        </div>
        <button onClick={onLogout} style={{ background:"rgba(255,255,255,.12)",border:"1px solid rgba(255,255,255,.2)",color:"#fff",padding:"5px 12px",borderRadius:8,cursor:"pointer",fontSize:12,fontWeight:600 }}>Déconnexion</button>
      </div>
      <div style={{ background:T.white,borderBottom:`1px solid ${T.border}`,display:"flex",overflowX:"auto",padding:"0 8px",position:"sticky",top:52,zIndex:199 }}>
        {tabs.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{ padding:"12px 10px",border:"none",background:"none",cursor:"pointer",fontSize:12,fontWeight:600,whiteSpace:"nowrap",color:tab===t.id?T.teal:T.gray2,borderBottom:tab===t.id?`2.5px solid ${T.teal}`:"2.5px solid transparent",display:"flex",alignItems:"center",gap:4 }}>
            {t.icon} {t.lbl}
            {t.notif>0&&<span style={{ background:T.warn,color:"#fff",borderRadius:10,padding:"1px 6px",fontSize:10,fontWeight:700 }}>{t.notif}</span>}
          </button>
        ))}
      </div>
      <div style={{ padding:16,maxWidth:600,margin:"0 auto" }}>
        {tab==="ppls"&&<PPL ppls={ppls} logements={[]} prestataires={[]} user={user}/>}
        {tab==="planning"&&<Planning ppls={ppls} user={user}/>}
        {tab==="taches"&&<Taches taches={taches} logements={[]} prestataires={[]} user={user}/>}
        {tab==="documents"&&<Documents documents={documents} prestataires={prestataires} user={user} docsObligatoires={{}}/>}
        {tab==="annonces"&&<Annonces annonces={annonces} prestataires={prestataires} user={user}/>}
      </div>
    </div>
  );
}

// ─── APP ──────────────────────────────────────────────────────────────────────
export default function App() {
  const [user,setUser] = useState(null);
  if(!user) return <Login onLogin={setUser}/>;
  if(user.role==="manager") return <ManagerPortal user={user} onLogout={()=>setUser(null)}/>;
  return <PrestatairePortal user={user} onLogout={()=>setUser(null)}/>;
}
